import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ListingPhoto } from "@/components/PhotoPlaceholder";
import { formatGBP, conditionLabel, computeFees, PLATFORM_FEE_PERCENT, FEE_TOOLTIP_COPY } from "@/lib/marketplace";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, Edit3, Users, PoundSterling, Box, Clock, ShieldCheck, Plus, Lock, ShoppingBag, Banknote, Wallet, Mail, Download, KeyRound, Info, Landmark, ArrowUpRight, Send, Loader2, ChevronDown, ChevronUp, ArrowDownLeft, Zap, CheckCircle, Copy, Check, Wand2, LogOut, AlertTriangle, Link2, Gamepad2, Star, HelpCircle, Leaf } from "lucide-react";
import useVerificationGate from "@/hooks/useVerificationGate";
import ParentVerificationModal from "@/components/ParentVerificationModal";
import PinPromptModal from "@/components/PinPromptModal";
import ConnectOnboardModal from "@/components/ConnectOnboardModal";
import { AddToHomeScreenBanner, AddToHomeScreenModal, usePostRegisterA2HSPrompt } from "@/components/AddToHomeScreen";
import FamilyWalletTile from "@/components/FamilyWalletTile";
import NotificationBell from "@/components/NotificationBell";
import PushEnableBanner from "@/components/PushEnableBanner";
// Iteration 124 — single unified Pending Queue drives both /dashboard/parent
// and /parent/moderation. Old inline moderation queue + PendingApprovalsInbox
// were split lists and are retired.
import EditListingModal from "@/components/EditListingModal";
import UnifiedPendingQueue from "@/components/UnifiedPendingQueue";
import PurchaseControlsCard from "@/components/PurchaseControlsCard";
import { subscribeListingUpdates, mergeListingInto, publishListingUpdate } from "@/lib/listingBus";
import { publishWalletUpdate, subscribeWalletUpdates } from "@/lib/walletBus";

function KidZoneQuickSwitch({ kids }) {
  const [open, setOpen] = useState(false);
  const list = (kids || []).filter((k) => k && k.id);
  if (list.length === 0) return null;
  return (
    <div className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        className="btn-secondary text-sm"
        data-testid="parent-kidzone-switch-btn"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Gamepad2 className="w-4 h-4" /> Kid Zone <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 z-30 w-56 bg-white rounded-2xl border border-slate-200 shadow-pop p-2"
          data-testid="parent-kidzone-switch-menu"
        >
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400 px-3 py-1">Jump into</div>
          {list.map((k) => (
            <Link
              key={k.id}
              to={`/kidzone?kid_id=${encodeURIComponent(k.id)}`}
              className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-brandPink/10 transition"
              data-testid={`parent-kidzone-switch-item-${k.id}`}
              role="menuitem"
              onMouseDown={(e) => e.preventDefault()}
            >
              <span className="text-xl">{k.avatar_emoji || "🧒"}</span>
              <span className="font-bold text-sm text-slate-900 truncate">{k.name}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function InfoTooltip({ label }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-200 text-slate-600 hover:bg-slate-300 transition"
        aria-label="More info about this fee"
        data-testid="fee-info-tooltip-trigger"
      >
        <Info className="w-3 h-3" />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute z-30 left-1/2 top-full -translate-x-1/2 mt-2 w-72 bg-brandSlate text-white text-xs font-normal leading-relaxed rounded-xl p-3 shadow-pop normal-case tracking-normal"
          data-testid="fee-info-tooltip-content"
        >
          {label}
        </span>
      )}
    </span>
  );
}

/**
 * Per-kid Stripe Connect status + "Link bank" CTA. Renders directly below
 * the Independent Banking toggle when the toggle is ON. Handles the three
 * upstream states (not_linked / pending / linked+payouts_enabled) plus the
 * age-gate 4xx (Stripe Express requires kid holders to be 18+).
 */
function KidConnectCTA({ kidId, kidName, status, busy, err, onLink, onRefresh }) {
  if (!status) {
    return (
      <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1" data-testid={`kid-connect-loading-${kidId}`}>
        <Loader2 className="w-3 h-3 animate-spin" /> Checking bank status…
      </div>
    );
  }
  const linked = status.payouts_enabled || status.linked || status.details_submitted;
  const pending = !linked && !!status.stripe_account_id;

  return (
    <div className="mt-2 space-y-1.5" data-testid={`kid-connect-${kidId}`}>
      {linked && (
        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200"
             data-testid={`kid-connect-linked-${kidId}`}>
          <CheckCircle className="w-3 h-3" /> Bank linked · payouts enabled
        </div>
      )}
      {!linked && (
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] text-slate-500 leading-tight">
            {pending
              ? `${kidName}'s Stripe onboarding hasn't finished.`
              : `Link a bank for ${kidName} so their earnings can be paid out.`}
          </div>
          <button
            type="button"
            onClick={onLink}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold transition disabled:opacity-60 flex-shrink-0"
            data-testid={`kid-link-bank-btn-${kidId}`}
          >
            {busy
              ? (<><Loader2 className="w-3 h-3 animate-spin" /> Opening Stripe…</>)
              : (<><Landmark className="w-3 h-3" /> {pending ? "Continue onboarding" : "Link bank"}</>)}
          </button>
        </div>
      )}
      {err && (
        <div className="text-[11px] text-rose-700" data-testid={`kid-connect-error-${kidId}`}>
          {err}
          <button
            type="button"
            onClick={onRefresh}
            className="ml-1 underline underline-offset-2"
            data-testid={`kid-connect-retry-${kidId}`}
          >Retry</button>
        </div>
      )}
    </div>
  );
}


export default function ParentDashboard() {
  const { user, refresh: refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  // Iteration 110 — canonical lifetime net earnings from the newly-
  // available upstream `/api/ledger/summary` endpoint. This is the
  // single source of truth for the "Earnings" stat card so the value
  // matches Kat's mobile app exactly (£3.00 net, not £3.30 gross).
  const [ledgerSummary, setLedgerSummary] = useState(null);
  // Bumped whenever the household wallet balance changes (top-up return,
  // parent→kid transfer, withdraw, quick send) so the FamilyWalletTile and
  // any other balance-sensitive child components re-fetch in place — no
  // hard refresh required.
  const [walletRefreshKey, setWalletRefreshKey] = useState(0);
  const bumpWallet = React.useCallback(() => setWalletRefreshKey((k) => k + 1), []);
  const [queue, setQueue] = useState([]);
  const [kids, setKids] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [editingListing, setEditingListing] = useState(null);
  const [err, setErr] = useState("");
  // Independent banking toggle — server-truth (upstream flag on Kid). Map is
  // rebuilt from /api/kids on every load; the toggle handler PATCHes upstream
  // and rolls back on failure.
  const [bankingMap, setBankingMap] = useState({});
  const [bankingBusyId, setBankingBusyId] = useState("");
  const [bankingError, setBankingError] = useState("");
  // Kid Stripe Connect — per-kid onboarding status map + busy/error state.
  // Lazily fetched only for kids that have `independent_banking_allowed`
  // flipped on, so we don't hammer the upstream connect endpoint for
  // every kid on every page load.
  const [kidConnectMap, setKidConnectMap] = useState({});
  const [kidConnectBusy, setKidConnectBusy] = useState("");
  const [kidConnectErr, setKidConnectErr] = useState({});
  // Resend verification email state — 'idle' | 'sending' | 'sent' | 'error'
  const [resendState, setResendState] = useState("idle");
  const [resendError, setResendError] = useState("");
  // Verify-email nudge banner dismissal (session-scoped so the parent sees
  // it again in a new tab or after signing out and back in).
  const [emailNudgeDismissed, setEmailNudgeDismissed] = useState(() => {
    try { return sessionStorage.getItem("mttApp:emailNudgeDismissed") === "1"; }
    catch (_) { return false; }
  });
  // Reset Kid Zone PIN modal — holds the kid being reset ({id, name}) or null.
  const [pinResetKid, setPinResetKid] = useState(null);
  const [pinResetValue, setPinResetValue] = useState("");
  const [pinResetBusy, setPinResetBusy] = useState(false);
  const [pinResetErr, setPinResetErr] = useState("");
  const [pinResetSuccess, setPinResetSuccess] = useState("");
  // Kid Zone handle picker — server-generated slug for handle-based login.
  const [handlePickerBusy, setHandlePickerBusy] = useState("");
  const [handlePickerErr, setHandlePickerErr] = useState({});
  const [copiedHandleFor, setCopiedHandleFor] = useState("");
  // Share Shop Link — per-kid clipboard confirmation flash on the sidebar
  // "Your kids" card. Uses REACT_APP_SITE_URL (production URL) so the copied
  // link always points to https://mytabletopapp.co.uk/shop/:kidId, even
  // when the parent is viewing the preview environment.
  const [sharedShopFor, setSharedShopFor] = useState("");
  const [shareShopErr, setShareShopErr] = useState({});
  // Revoke kid sessions — parent-authed POST /kids/{id}/revoke-sessions
  const [revokeBusy, setRevokeBusy] = useState("");
  const [revokeConfirmKid, setRevokeConfirmKid] = useState(null);
  const [revokeError, setRevokeError] = useState("");
  const lastRevokeNameRef = useRef("");
  const [regenerateConfirmKid, setRegenerateConfirmKid] = useState(null);
  // Waitlist
  // Iteration 99 — admin CSV exports (waitlist + platform fees) moved
  // to /admin. `waitlistCount`, `exporting`, `exportError`, `feeSummary`
  // and related handlers are no longer needed on the Parent Dashboard.
  // Per-transaction wallet payouts
  // Iteration 58 — derive payout-linked state from the canonical backend
  // flag (`is_bank_linked` on /wallet/connect/status and /auth/me) instead
  // of localStorage. Local storage was per-device and caused mobile users
  // to keep seeing the "Link Bank" callout even after linking on desktop.
  const [payouts, setPayouts] = useState([]);
  // Wallet top-up + transfer-to-kid controls
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState("10");
  const [topUpBusy, setTopUpBusy] = useState(false);
  const [topUpError, setTopUpError] = useState("");
  const [topUpSuccessMsg, setTopUpSuccessMsg] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferKidId, setTransferKidId] = useState("");
  const [transferAmount, setTransferAmount] = useState("5");
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [transferSuccess, setTransferSuccess] = useState("");
  // PIN gate — reusable across "Send now" transfer + per-kid "Send £1" quick action
  const [pinOpen, setPinOpen] = useState(false);
  const [pinAction, setPinAction] = useState(null); // { kind, kidId, kidName, pennies }
  // Transaction ledger (household wallet)
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerTxns, setLedgerTxns] = useState([]);
  const [ledgerError, setLedgerError] = useState("");
  const [ledgerLoaded, setLedgerLoaded] = useState(false);
  // One-click notify-me on the launch banner
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);
  const [notifyError, setNotifyError] = useState("");
  // Iteration 95 — in-app "ping" surfaced when a kid submits an edit and the
  // parent tab is open. Fired via BroadcastChannel (`listingBus.js`) so it
  // works cross-tab on the same device. Cleared automatically after 6s or
  // when the parent clicks the banner (which jumps to the Moderation Queue).
  const [kidEditPing, setKidEditPing] = useState(null); // { title, listing_id, kid_alias, at }
  const kidEditPingTimerRef = useRef(null);
  const seenListingRevsRef = useRef(new Map()); // debounces repeat broadcasts
  const dismissKidEditPing = useCallback(() => {
    setKidEditPing(null);
    if (kidEditPingTimerRef.current) clearTimeout(kidEditPingTimerRef.current);
  }, []);
  const jumpToModerationQueue = useCallback(() => {
    const target = document.getElementById("moderation-queue");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      try { target.classList.add("ring-2", "ring-brandPink", "ring-offset-2"); } catch (_) { /* ignore */ }
      setTimeout(() => {
        try { target.classList.remove("ring-2", "ring-brandPink", "ring-offset-2"); } catch (_) { /* ignore */ }
      }, 1800);
    }
    dismissKidEditPing();
  }, [dismissKidEditPing]);
  // Withdraw / payout modal
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawPin, setWithdrawPin] = useState("");
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const [withdrawError, setWithdrawError] = useState("");
  const [withdrawErrorCode, setWithdrawErrorCode] = useState("");
  const [withdrawSuccess, setWithdrawSuccess] = useState("");
  // Stripe Connect Express — bank linking
  const [connectBusy, setConnectBusy] = useState(false);
  const [connectError, setConnectError] = useState("");
  const [connectFlash, setConnectFlash] = useState("");
  // Embedded Components modal state
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  // Iteration 64 — prime `connectStatus` from the AuthContext user so the
  // first render already knows if the bank is linked. Eliminates the
  // sub-second flash of the "Link your bank" banner while the mount
  // fetch is in flight. `null` = still loading (used to gate the CTA render).
  const [connectStatus, setConnectStatus] = useState(() => {
    if (!user) return null;
    if (user.is_bank_linked || user.bank_last4) {
      return {
        is_bank_linked: true,
        details_submitted: !!user.is_bank_linked,
        payouts_enabled: false,
        phase_b_enabled: true,
      };
    }
    return null;
  });
  // De-dupe dashboard loads across AuthProvider's login→refresh double-setUser
  // (see useEffect below). Ref, not state, so it doesn't trigger re-renders.
  const loadedForParentIdRef = useRef(null);
  const { gate: gateVerification, modalProps: verificationModalProps } = useVerificationGate();
  // Direct-open "Get Adult Verified" modal — bypasses the gate's isVerified
  // check so the first click always opens the modal even if upstream flags
  // haven't hydrated yet.
  const [idVerifyOpen, setIdVerifyOpen] = useState(false);
  // Post-registration "Add to Home Screen" prompt (mobile only).
  const { open: a2hsOpen, setOpen: setA2hsOpen } = usePostRegisterA2HSPrompt();

  const load = async () => {
    try {
      const [s, q, k, ord, sum] = await Promise.all([
        api.get("/dashboard/parent"),
        api.get("/listings/mod/queue"),
        api.get("/kids"),
        api.get("/orders").catch(() => ({ data: [] })),
        // Iteration 110 — new canonical ledger summary endpoint.
        // Gracefully fall back to {} if upstream is still catching up
        // for any given parent so the rest of the dashboard renders.
        api.get("/ledger/summary").catch(() => ({ data: {} })),
      ]);
      setStats(s.data); setQueue(q.data); setKids(k.data);
      setLedgerSummary(sum.data || {});
      // Iteration 108 — dashboard table now shows Sales & Purchases
      // (both sides of every household transaction). Dedupe by
      // transaction key so upstream perspective rows don't render
      // Kat's £3 sale twice OR Tom's £3.30 purchase twice.
      const paidAll = (ord.data || []).filter((o) => ["paid", "shipped", "collected"].includes(o.status));
      const seen = new Set();
      const paid = [];
      for (const o of paidAll) {
        // Key by transaction id + role so a single upstream order's
        // two perspective rows survive as 2 rows (one Sold, one
        // Bought) but true duplicates are collapsed.
        const key = `${o.order_group_id || o.transaction_id || o.id}::${o.role || (o.seller_kid_id === o.buyer_kid_id ? "self" : (o.seller_kid_id ? "sold" : "bought"))}`;
        if (seen.has(key)) continue;
        seen.add(key);
        paid.push(o);
      }
      setPayouts(paid);
      const map = {};
      (k.data || []).forEach((kid) => { map[kid.id] = !!kid.independent_banking_allowed; });
      setBankingMap(map);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => {
    // Gate the dashboard fetches on auth-state hydration AND de-dupe across
    // AuthProvider's double setUser (login response → /auth/me refresh).
    // The ref guarantees load() fires exactly once per parent_id per mount,
    // even when React re-renders with an equal-id user object.
    if (!user || user === false || !user.id) return;
    if (loadedForParentIdRef.current === user.id) return;
    loadedForParentIdRef.current = user.id;
    load();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iteration 76 — real-time dashboard stats. Auto-refresh the stats
  // whenever the tab regains focus (parent switches back from Stripe,
  // approving an order, force-releasing a listing, etc.) so Earnings /
  // Pending / Live / Sold cards recalculate immediately. Also polls
  // silently every 45s while the tab is active as a belt-and-suspenders.
  useEffect(() => {
    if (!user?.id) return;
    const softRefresh = async () => {
      try {
        const s = await api.get("/dashboard/parent");
        setStats(s.data);
        // Iteration 110 — keep the Earnings card in sync with the
        // canonical ledger summary alongside the dashboard aggregates.
        try {
          const sum = await api.get("/ledger/summary");
          setLedgerSummary(sum.data || {});
        } catch { /* silent — dashboard aggregate is the fallback */ }
      } catch { /* silent — full load() covers hard errors */ }
    };
    const onFocus = () => { softRefresh(); };
    window.addEventListener("focus", onFocus);
    const t = setInterval(softRefresh, 45000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(t);
    };
  }, [user?.id]);

  // Cross-tab / cross-surface revalidation for the moderation queue.
  useEffect(() => {
    return subscribeListingUpdates((updated) => {
      setQueue((prev) => mergeListingInto(prev, updated));
      // Iteration 91 — any listing status transition (kid edit → pending,
      // parent re-approve → live) needs to refresh the Pending Approvals
      // stat card + inbox, so also bounce the top-level stats load.
      if (updated?.status || updated?.awaiting_parent_reapproval !== undefined) {
        load();
      }
      // Iteration 95 — the moment a kid saves an edit in another tab,
      // dispatch an in-app ping so the parent sees the notification
      // banner without needing to refresh. We debounce identical
      // broadcasts (React StrictMode + BroadcastChannel echoes) via
      // a per-listing "last seen" map keyed on the listing id + status.
      if (updated?.awaiting_parent_reapproval === true) {
        const key = `${updated.id || "unknown"}::${updated.status || ""}`;
        const seen = seenListingRevsRef.current;
        const last = seen.get(key);
        const now = Date.now();
        if (last && now - last < 4000) return;
        seen.set(key, now);
        setKidEditPing({
          title: updated.title || "a listing",
          listing_id: updated.id,
          kid_alias: updated.kid_alias || "your kid",
          at: now,
        });
        if (kidEditPingTimerRef.current) clearTimeout(kidEditPingTimerRef.current);
        kidEditPingTimerRef.current = setTimeout(() => setKidEditPing(null), 8000);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wallet + passbook event bus — a completed sale in another tab or a
  // fresh kid deposit should re-pull the parent dashboard stats so the
  // "Total Sales" card, kids list, and family wallet all stay live.
  useEffect(() => {
    return subscribeWalletUpdates(() => {
      load();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-emptive Phase B feature-flag check — fetch once on mount so we know
  // whether to render the "Link my bank" CTAs or a "Coming soon" note. Errors
  // are swallowed (assume disabled) so a transient network hiccup never leaks
  // a broken button to the parent.
  const refetchConnectStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/wallet/connect/status");
      setConnectStatus(data || {});
      return data || {};
    } catch (_) {
      // Iteration 64 — on error, don't clobber a primed / previously-good
      // linked status. Fall through to an empty object so the UI treats
      // the state as "known unlinked" (not "still loading").
      setConnectStatus((prev) => prev?.is_bank_linked ? prev : { phase_b_enabled: false });
      return null;
    }
  }, []);

  useEffect(() => { refetchConnectStatus(); }, [refetchConnectStatus]);

  // Iteration 60 — the moment the tab regains focus (typical after returning
  // from a Stripe onboarding redirect on the same device), re-poll the bank
  // status so the "Bank linked" pill flips instantly, no manual refresh.
  useEffect(() => {
    const onFocus = () => { refetchConnectStatus(); refreshUser?.(); };
    const onVisibility = () => {
      if (document.visibilityState === "visible") onFocus();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refetchConnectStatus, refreshUser]);

  const phaseBEnabled = !!connectStatus?.phase_b_enabled;
  // Iteration 58 — the canonical `is_bank_linked` flag comes straight from
  // the backend and is identical across desktop / mobile / any future
  // client. Fall back to legacy signals only for graceful old-payload
  // support so already-linked users never see the CTA regress.
  const bankLinked = !!(
    connectStatus?.is_bank_linked
    || connectStatus?.details_submitted
    || connectStatus?.payouts_enabled
    || user?.is_bank_linked
    || user?.bank_last4
  );
  // Copy the mobile team returns on the status endpoint when Phase B is off —
  // surfaces upstream messaging verbatim if it exists.
  const phaseBOffMessage = connectStatus?.message
    || "Automated Stripe payouts arrive in Phase B — for now our ops team settles withdrawals via manual bank transfer.";

  // Detect return from Stripe top-up: /dashboard/parent?topup_session=<id>[&status=success|cancel]
  // Poll status, then refresh the user object so the balance updates.
  useEffect(() => {
    const sid = searchParams.get("topup_session");
    if (!sid) return;
    (async () => {
      try {
        const { data } = await api.get(`/wallet/topup/status/${sid}`);
        const paid = data?.payment_status === "paid" || data?.status === "complete" || data?.status === "paid";
        if (paid) {
          setTopUpSuccessMsg(`Top-up successful${data?.amount_pennies ? ` — ${formatGBP(data.amount_pennies / 100)} added`: ""}.`);
        } else if (data?.status === "cancelled" || searchParams.get("status") === "cancel") {
          setTopUpSuccessMsg("Top-up cancelled.");
        }
      } catch (_) { /* silent — user can retry */ }
      await refreshUser?.();
      // Force downstream balance-sensitive tiles (Family Wallet, etc.) to
      // re-fetch alongside the parent user object so the whole dashboard
      // reflects the new balance without a hard refresh.
      bumpWallet();
      // Clean the query params so a browser refresh doesn't re-run this
      const next = new URLSearchParams(searchParams);
      next.delete("topup_session"); next.delete("status");
      setSearchParams(next, { replace: true });
      setTimeout(() => setTopUpSuccessMsg(""), 6000);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link intents from other pages (e.g. /dashboard/ledger summary CTAs):
  //   ?topup=1        -> open Household Top-Up modal
  //   ?withdraw=1     -> open Withdraw modal (goes through identity/gate flow)
  //   ?send_to=<kid>  -> open the Family Wallet quick top-up for that kid
  //   ?resetpin=<kid> -> open the Reset Kid PIN dialog for that kid
  // Gated on `stats` so the wallet is fully loaded before we open a modal
  // that reads walletAvailablePence.
  useEffect(() => {
    if (!stats) return;
    const wantTopup = searchParams.get("topup") === "1";
    const wantWithdraw = searchParams.get("withdraw") === "1";
    const sendTo = searchParams.get("send_to");
    const resetPinKid = searchParams.get("resetpin");
    if (!wantTopup && !wantWithdraw && !sendTo && !resetPinKid) return;
    const next = new URLSearchParams(searchParams);
    if (wantTopup) {
      next.delete("topup");
      openTopUp();
    }
    if (wantWithdraw) {
      next.delete("withdraw");
      onWithdrawClick();
    }
    if (sendTo) {
      next.delete("send_to");
      const kid = (kids || []).find((k) => k.id === sendTo);
      if (kid) {
        // Reuse the PIN-gated household transfer flow with a £1 default;
        // parents can tweak the amount in the modal that follows.
        quickTopUp(kid, 100);
      }
    }
    if (resetPinKid) {
      next.delete("resetpin");
      const kid = (kids || []).find((k) => k.id === resetPinKid);
      if (kid) setPinResetKid(kid);
    }
    setSearchParams(next, { replace: true });
  }, [stats]); // eslint-disable-line react-hooks/exhaustive-deps

  const walletAvailablePence = user?.wallet_available_pennies ?? 0;
  const walletPendingPence = user?.wallet_pending_pennies ?? 0;
  const walletPayoutRequestedPence = user?.wallet_payout_requested_pennies ?? 0;

  const openTopUp = () => {
    setTopUpError(""); setTopUpAmount("10"); setTopUpOpen(true);
  };

  const submitTopUp = async () => {
    setTopUpError("");
    const pennies = Math.round(parseFloat(topUpAmount) * 100);
    if (!Number.isFinite(pennies) || pennies < 100 || pennies > 50000) {
      setTopUpError("Amount must be between £1 and £500.");
      return;
    }
    setTopUpBusy(true);
    try {
      const originUrl = `${window.location.origin}/dashboard/parent`;
      const { data } = await api.post("/wallet/topup", { amount_pennies: pennies, origin_url: originUrl });
      // Upstream returns `checkout_url` (not `url`) — accept both for
      // resilience against upstream renames.
      const url = data?.checkout_url || data?.url;
      if (url) {
        // Redirect to Stripe Checkout for the top-up
        window.location.href = url;
        return;
      }
      // Surface any structured error detail from upstream so support can see
      // exactly why no URL was returned (e.g. Stripe key rejected).
      const raw = JSON.stringify(data || {});
      setTopUpError(`Could not start top-up — no checkout URL received. Server said: ${raw.slice(0, 240)}`);
    } catch (e) {
      // Surface upstream's structured `detail.message` verbatim so we can
      // see exactly why the checkout failed (invalid Stripe key, missing
      // origin_url, rate limit, etc.).
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "object" && detail?.message)
        ? detail.message
        : (typeof detail === "string" ? detail : null);
      setTopUpError(msg || formatApiError(e) || "Top-up failed.");
    } finally { setTopUpBusy(false); }
  };

  const openTransfer = () => {
    setTransferError(""); setTransferSuccess("");
    setTransferAmount("5");
    setTransferKidId(kids?.[0]?.id || "");
    setTransferOpen(true);
  };

  const submitTransfer = () => {
    // 1) Validate + open the PIN gate. The actual API call runs from
    //    executeTransfer() once the parent's PIN is verified.
    setTransferError(""); setTransferSuccess("");
    if (!transferKidId) { setTransferError("Please select a child."); return; }
    const pennies = Math.round(parseFloat(transferAmount) * 100);
    if (!Number.isFinite(pennies) || pennies < 1 || pennies > 50000) {
      setTransferError("Amount must be between £0.01 and £500.");
      return;
    }
    if (pennies > walletAvailablePence) {
      setTransferError(`Only ${formatGBP(walletAvailablePence / 100)} available — top up first.`);
      return;
    }
    const kidName = kids.find((k) => k.id === transferKidId)?.name || "child";
    setPinAction({ kind: "transfer-modal", kidId: transferKidId, kidName, pennies });
    setPinOpen(true);
  };

  // Actually POST the transfer after the PIN gate resolves. Errors bubble to
  // PinPromptModal via a throw, then re-surface inline on the transfer modal.
  const executeTransfer = async ({ kidId, kidName, pennies, kind }) => {
    setTransferBusy(true);
    try {
      await api.post("/wallet/transfer-to-kid", { kid_id: kidId, amount_pennies: pennies });
      await refreshUser?.();
      // Refresh kids so per-card wallet_pennies updates too
      try {
        const { data: freshKids } = await api.get("/kids");
        setKids(freshKids);
      } catch (_) { /* non-fatal */ }
      // Trigger downstream Family Wallet tile re-fetch so the per-kid card
      // balance under it syncs immediately with the /kids call above.
      bumpWallet();
      // Iteration 87 — cross-tab wallet event so any open Kid Zone tab
      // refreshes the receiving kid's passbook + wallet balance instantly.
      publishWalletUpdate({ scope: "kid", reason: "parent_transfer", kid_id: kidId });
      if (kind === "quick-send") {
        setTopUpSuccessMsg(`Sent ${formatGBP(pennies / 100)} to ${kidName}.`);
        setTimeout(() => setTopUpSuccessMsg(""), 4500);
      } else {
        setTransferSuccess(`Sent ${formatGBP(pennies / 100)} to ${kidName}.`);
        setTimeout(() => setTransferOpen(false), 1500);
      }
    } finally {
      setTransferBusy(false);
    }
  };

  // Quick-action button on each kid card: send £1 in two clicks (button + PIN).
  const quickSendPound = (kid) => {
    if (walletAvailablePence < 100) {
      setTopUpSuccessMsg(`Not enough in the household wallet — top up first.`);
      setTimeout(() => setTopUpSuccessMsg(""), 4500);
      return;
    }
    setPinAction({ kind: "quick-send", kidId: kid.id, kidName: kid.name, pennies: 100 });
    setPinOpen(true);
  };

  // Family Wallet Tile handler — parametric variant of quickSendPound so a
  // parent can send any amount from the household wallet in one PIN prompt.
  const quickTopUp = (kid, pennies) => {
    if (pennies <= 0) return;
    if (walletAvailablePence < pennies) {
      setTopUpSuccessMsg(`Not enough in the household wallet — top up first.`);
      setTimeout(() => setTopUpSuccessMsg(""), 4500);
      return;
    }
    setPinAction({ kind: "quick-send", kidId: kid.id, kidName: kid.name, pennies });
    setPinOpen(true);
  };

  // Called once PIN verification succeeds inside PinPromptModal.
  const onPinVerified = async () => {
    if (!pinAction) return;
    await executeTransfer(pinAction);
  };

  // Transaction ledger — lazy fetch on first expand.
  const toggleLedger = async () => {
    const next = !ledgerOpen;
    setLedgerOpen(next);
    if (next && !ledgerLoaded) {
      setLedgerLoading(true); setLedgerError("");
      try {
        // Iteration 114 — cache-bust so browsers never hand back a
        // stale duplicated passbook payload from before iteration 113
        // shipped. Backend also stamps Cache-Control: no-store.
        const { data } = await api.get(`/wallet/history?limit=25&_t=${Date.now()}`);
        setLedgerTxns(Array.isArray(data?.transactions) ? data.transactions : (Array.isArray(data) ? data : []));
        setLedgerLoaded(true);
      } catch (e) {
        setLedgerError(formatApiError(e) || "Could not load transactions.");
      } finally { setLedgerLoading(false); }
    }
  };

  // One-click "Notify me at {user.email}" — direct POST to /api/waitlist.
  const notifyMe = async () => {
    if (!user?.email) return;
    setNotifyBusy(true); setNotifyError("");
    try {
      await api.post("/waitlist", { email: user.email, store: "both" });
      setNotifyDone(true);
    } catch (e) {
      setNotifyError(formatApiError(e) || "Could not sign you up — try again.");
    } finally { setNotifyBusy(false); }
  };

  // -----------------------------------------------------------------
  // Stripe Connect Express — bank linking (Embedded Components).
  //
  // Bank details are captured *inside* Stripe's iframe on this same page —
  // no redirects, no bank data in our React state or MongoDB. All Stripe SDK
  // calls (accounts.create, accountLinks.create, payouts.create) remain on
  // the mobile backend; the web only forwards an Account Session client_secret.
  // -----------------------------------------------------------------
  // On mobile/small viewports OR when the embedded modal init fails, we
  // redirect straight to Stripe's hosted onboarding via POST
  // /api/wallet/connect/onboard. This avoids the WebKit/iframe auth issue
  // where the embedded Stripe Connect flow errors out on iOS Safari.
  const isMobileViewport = () => (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 767px)").matches);

  const redirectToHostedOnboarding = async () => {
    try {
      setConnectFlash("Opening Stripe onboarding…");
      const returnBase = (typeof window !== "undefined" ? window.location.origin : "");
      const { data } = await api.post("/wallet/connect/onboard", {
        return_url: `${returnBase}/dashboard/parent?stripe_return=true`,
        refresh_url: `${returnBase}/dashboard/parent?stripe_return=refresh`,
      });
      if (data?.url) {
        window.location.href = data.url;
        return true;
      }
      setConnectError("Stripe onboarding link unavailable. Please try again in a moment.");
      setConnectFlash("");
      return false;
    } catch (e) {
      setConnectError(formatApiError(e) || "Could not open Stripe onboarding.");
      setConnectFlash("");
      return false;
    }
  };

  // Iteration 63 — REMOVED the mobile-viewport branch. Both desktop and
  // mobile now redirect to Stripe's hosted onboarding page. The embedded
  // ConnectOnboardModal was surfacing "Something went wrong · error
  // during authentication" for signed-in parents on desktop as well as
  // WebKit mobile Safari, so we route every device through the same
  // proven hosted flow.
  const startConnectOnboarding = async () => {
    setConnectError(""); setConnectFlash("");
    await redirectToHostedOnboarding();
  };

  const onConnectExit = async () => {
    setConnectFlash("Verifying your bank details with Stripe…");
    const data = await refetchConnectStatus();
    if (data?.payouts_enabled || data?.is_bank_linked) {
      setConnectFlash("Bank linked. Withdrawals are now enabled.");
    } else if (data?.details_submitted) {
      setConnectFlash("Bank details submitted — Stripe is still reviewing.");
    } else {
      setConnectFlash("Onboarding closed — resume any time from Link my bank.");
    }
    await refreshUser?.();
    setTimeout(() => setConnectFlash(""), 8000);
  };

  // Iteration 60 — Poll the status endpoint briefly right after the modal
  // closes so the "Bank linked" pill appears without waiting for the next
  // focus event. Stripe's `onExit` fires immediately once the user closes
  // the dialog even if their onboarding hasn't fully propagated on their
  // side, so we short-poll for up to ~10 seconds.
  useEffect(() => {
    if (connectModalOpen) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled || attempts++ >= 5) return;
      const data = await refetchConnectStatus();
      if (data?.is_bank_linked) return; // stop early on success
      setTimeout(tick, 2000);
    };
    // Only start the short-poll if we've previously opened the modal in
    // this session — no need to hammer the endpoint for parents who never
    // touched the flow. We track that via `connectFlash` being non-empty
    // right after `onConnectExit` fires.
    if (connectFlash) setTimeout(tick, 1500);
    return () => { cancelled = true; };
  }, [connectModalOpen, connectFlash, refetchConnectStatus]);

  // Detect ?connect=return|refresh and ?stripe_return=true landings — the
  // latter is the iteration 61 hosted-onboarding fallback used on mobile.
  useEffect(() => {
    const flag = searchParams.get("connect");
    const stripeReturn = searchParams.get("stripe_return");
    // Iteration 295 — Safari/iOS: onboarding return URLs carry
    // connect_callback=true&user_id=<parent> so a dropped session cookie
    // doesn't hide a completed bank link.
    const connectCallback = searchParams.get("connect_callback");
    if (!flag && !stripeReturn && !connectCallback) return;
    if (stripeReturn === "true") setConnectFlash("Welcome back from Stripe! Refreshing your bank status…");
    else if (stripeReturn === "refresh") setConnectFlash("Onboarding cancelled — tap Link my bank to resume.");
    else if (flag === "return") setConnectFlash("Welcome back! Refreshing your bank status…");
    else if (flag === "refresh") setConnectFlash("Onboarding cancelled — click Link my bank to resume.");
    (async () => {
      // Ask Stripe directly first (works even if the session cookie was
      // dropped by ITP), then poll status as before.
      if (connectCallback === "true") {
        try {
          await api.post("/wallet/connect/verify-return", { user_id: searchParams.get("user_id") || undefined });
        } catch { /* fall through to the status polling below */ }
      }
      // Poll a couple of times because Stripe's webhook can lag a few
      // seconds behind the redirect. This ensures the green pill appears
      // without a manual refresh (iteration 61 spec).
      let ok = false;
      for (let i = 0; i < 4 && !ok; i++) {
        const data = await refetchConnectStatus();
        if (data?.is_bank_linked) { ok = true; break; }
        await new Promise((r) => setTimeout(r, 1500));
      }
      await refreshUser?.();
      const next = new URLSearchParams(searchParams);
      next.delete("connect");
      next.delete("stripe_return");
      next.delete("connect_callback");
      next.delete("user_id");
      setSearchParams(next, { replace: true });
      setConnectFlash(ok ? "Bank linked. Withdrawals are now enabled." : "Stripe is still verifying — refresh in a moment.");
      setTimeout(() => setConnectFlash(""), 8000);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------------------------------------
  // Withdraw / payout — Phase A wiring against POST /api/wallet/payout
  // -----------------------------------------------------------------
  const openWithdraw = () => {
    setWithdrawError(""); setWithdrawErrorCode(""); setWithdrawSuccess("");
    setWithdrawAmount(""); setWithdrawPin("");
    setWithdrawOpen(true);
  };

  // Adult-identity gate: gateVerification runs the callback immediately if
  // identity_verified/is_adult_verified is true, otherwise opens the existing
  // Parent Verification (selfie) modal and re-invokes the callback on success.
  // Iteration 71 — bank-verified parents (Stripe Connect KYC already done)
  // skip the selfie modal on wallet flows. `bankLinked` here is the same
  // canonical composite the header pill uses.
  const onWithdrawClick = () => {
    const adultOk = !!(
      user?.is_adult_verified
      || user?.identity_verified
      || user?.is_bank_linked
      || user?.stripe_account_status === "verified"
      || user?.bank_last4
      || bankLinked
    );
    if (!adultOk) {
      gateVerification(openWithdraw, { reason: "before you withdraw funds from your wallet" });
      return;
    }
    openWithdraw();
  };

  const submitWithdraw = async () => {
    setWithdrawError(""); setWithdrawErrorCode(""); setWithdrawSuccess("");
    if (!/^\d{4}$/.test(withdrawPin)) {
      setWithdrawError("PIN must be exactly 4 digits."); return;
    }
    let payoutPennies = null; // null = full drain
    const raw = String(withdrawAmount || "").trim();
    if (raw !== "") {
      const p = Math.round(parseFloat(raw) * 100);
      if (!Number.isFinite(p) || p < 100 || p > 50000) {
        setWithdrawError("Amount must be between £1.00 and £500.00 — or leave blank to withdraw everything.");
        return;
      }
      if (p > walletAvailablePence) {
        setWithdrawError(`Only ${formatGBP(walletAvailablePence / 100)} available.`);
        return;
      }
      payoutPennies = p;
    } else if (walletAvailablePence <= 0) {
      setWithdrawError("Nothing to withdraw — top up your wallet first."); return;
    }
    setWithdrawBusy(true);
    const body = { pin: withdrawPin };
    if (payoutPennies != null) body.payout_pennies = payoutPennies;
    try {
      const { data } = await api.post("/wallet/payout", body);
      const draining = payoutPennies == null;
      const amountText = draining
        ? formatGBP(walletAvailablePence / 100)
        : formatGBP(payoutPennies / 100);
      setWithdrawSuccess(`Payout of ${amountText} submitted. It will land in your linked bank account shortly.`);
      // Optimistically prepend the pending payout to the ledger so parents see
      // immediate feedback in the Recent Activity list.
      const optimistic = {
        id: data?.id || data?.payout_id || `optimistic-${Date.now()}`,
        type: "payout",
        status: "payout_pending",
        direction: "debit",
        amount_pennies: -(payoutPennies ?? walletAvailablePence),
        description: draining ? "Payout (full balance)" : "Payout to linked bank",
        created_at: new Date().toISOString(),
      };
      setLedgerTxns((prev) => [optimistic, ...prev]);
      setLedgerLoaded(true); // ensure the collapsible has data to show
      await refreshUser?.();
      bumpWallet();
      setTimeout(() => setWithdrawOpen(false), 1600);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" && detail?.code ? detail.code : "";
      setWithdrawErrorCode(code);
      // Prefer structured message; fall back to formatter
      const msg = (typeof detail === "object" && detail?.message)
        ? detail.message
        : (formatApiError(e) || "Withdrawal failed. Please try again.");
      // Friendlier copy for well-known codes
      const friendly = {
        INVALID_PIN: "That PIN is incorrect. Please try again.",
        INSUFFICIENT_FUNDS: "Not enough available balance to cover this withdrawal.",
        LIMIT_EXCEEDED: "This withdrawal exceeds your current payout limit.",
        RATE_LIMITED: "Too many attempts — please wait a minute and try again.",
      };
      setWithdrawError(friendly[code] || msg);
      if (code === "INVALID_PIN") setWithdrawPin("");
    } finally { setWithdrawBusy(false); }
  };

  const toggleBanking = async (kidId) => {
    if (bankingBusyId) return;
    const prev = !!bankingMap[kidId];
    const next = !prev;
    // Optimistic update
    setBankingBusyId(kidId);
    setBankingError("");
    setBankingMap((m) => ({ ...m, [kidId]: next }));
    try {
      const resp = await api.patch(`/kids/${kidId}/independent-banking`, { enabled: next });
      // Trust the server response in case any policy caused a different outcome.
      const authoritative = !!resp?.data?.independent_banking_allowed;
      setBankingMap((m) => ({ ...m, [kidId]: authoritative }));
    } catch (e) {
      // Roll back
      setBankingMap((m) => ({ ...m, [kidId]: prev }));
      setBankingError(formatApiError(e) || "Could not update independent banking.");
    } finally {
      setBankingBusyId("");
    }
  };

  const handleResendVerification = async () => {
    if (resendState === "sending" || resendState === "sent") return;
    setResendState("sending");
    setResendError("");
    try {
      await api.post("/auth/resend-verification");
      setResendState("sent");
      // Reset back to idle after 30s so parents can resend again if needed.
      setTimeout(() => setResendState("idle"), 30000);
    } catch (e) {
      setResendState("error");
      setResendError(formatApiError(e) || "Could not send verification email. Please try again shortly.");
    }
  };

  const submitPinReset = async () => {
    if (!pinResetKid || pinResetBusy) return;
    if (!/^\d{4,8}$/.test(pinResetValue)) {
      setPinResetErr("PIN must be 4–8 digits, numbers only.");
      return;
    }
    setPinResetBusy(true);
    setPinResetErr("");
    try {
      await api.post(`/kids/${pinResetKid.id}/pin`, { pin: pinResetValue });
      // Success — show success banner and close modal
      setPinResetSuccess(`New Kid Zone PIN set for ${pinResetKid.name}. Any existing kid sessions have been signed out.`);
      setPinResetKid(null);
      setPinResetValue("");
      setTimeout(() => setPinResetSuccess(""), 8000);
    } catch (e) {
      setPinResetErr(formatApiError(e) || "Couldn't update the PIN. Please try again.");
    } finally {
      setPinResetBusy(false);
    }
  };

  const generateHandle = async (kidId) => {
    if (handlePickerBusy) return;
    setHandlePickerBusy(kidId);
    setHandlePickerErr((e) => ({ ...e, [kidId]: "" }));
    try {
      const { data } = await api.post(`/kids/${kidId}/ensure-handle`);
      // Refresh the kids list so k.handle updates in the map above.
      const kidsResp = await api.get("/kids");
      setKids(kidsResp.data || []);
      // Immediate feedback: the success banner announces the new handle so
      // parents can share it with their child.
      const newHandle = data?.handle;
      if (newHandle) {
        setPinResetSuccess(`Kid Zone handle for this kid: @${newHandle} — share it with them so they can sign in on any device.`);
        setTimeout(() => setPinResetSuccess(""), 10000);
      }
    } catch (e) {
      setHandlePickerErr((prev) => ({ ...prev, [kidId]: formatApiError(e) || "Couldn't generate a handle. Please try again." }));
    } finally {
      setHandlePickerBusy("");
    }
  };

  const copyHandle = async (handle) => {
    if (!handle) return;
    try {
      // Align with Kid Workspace clipboard semantics — always copy the @-prefixed
      // form so the pasted value reads naturally in messages / notes.
      await navigator.clipboard.writeText(`@${handle}`);
      setCopiedHandleFor(handle);
      setTimeout(() => setCopiedHandleFor(""), 2000);
    } catch (_e) { /* clipboard unavailable in some browsers — silent no-op */ }
  };

  const shareKidShopLink = async (kid) => {
    if (!kid?.id) return;
    setShareShopErr((prev) => ({ ...prev, [kid.id]: "" }));
    const siteBase = (process.env.REACT_APP_SITE_URL || "https://mytabletopapp.co.uk").replace(/\/$/, "");
    const shopUrl = `${siteBase}/shop/${kid.id}`;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shopUrl);
      } else {
        // Fallback for older browsers / insecure contexts
        const ta = document.createElement("textarea");
        ta.value = shopUrl;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute"; ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setSharedShopFor(kid.id);
      setTopUpSuccessMsg(`Copied ${kid.name}'s shop link — paste it anywhere to share their storefront.`);
      setTimeout(() => setSharedShopFor(""), 2200);
      setTimeout(() => setTopUpSuccessMsg(""), 4500);
    } catch (_e) {
      setShareShopErr((prev) => ({ ...prev, [kid.id]: "Clipboard unavailable — long-press the link to copy manually." }));
    }
  };

  const revokeKidSessions = async (kid) => {
    if (revokeBusy) return;
    setRevokeBusy(kid.id);
    setRevokeError("");
    try {
      await api.post(`/kids/${kid.id}/revoke-sessions`);
      setPinResetSuccess(`Signed ${kid.name} out on every device. Their next Kid Zone login will need the current PIN.`);
      setTimeout(() => setPinResetSuccess(""), 8000);
      setRevokeConfirmKid(null);
    } catch (e) {
      const detail = formatApiError(e) || "";
      const kidLabel = kid?.name || "your kid";
      setRevokeError(detail
        ? `Couldn't sign ${kidLabel} out on all devices — ${detail}`
        : `Couldn't sign ${kidLabel} out on all devices. Please try again.`);
      setTimeout(() => setRevokeError(""), 8000);
      setRevokeConfirmKid(null);
    } finally {
      setRevokeBusy("");
    }
  };

  const refreshKidConnect = async (kidId) => {
    try {
      const { data } = await api.get(`/kids/${kidId}/connect/status`);
      setKidConnectMap((m) => ({ ...m, [kidId]: data }));
      setKidConnectErr((e) => ({ ...e, [kidId]: "" }));
    } catch (e) {
      setKidConnectErr((prev) => ({ ...prev, [kidId]: formatApiError(e) || "Could not fetch bank status." }));
    }
  };

  const linkKidBank = async (kidId) => {
    if (kidConnectBusy) return;
    setKidConnectBusy(kidId);
    setKidConnectErr((e) => ({ ...e, [kidId]: "" }));
    try {
      const origin = window.location.origin;
      const { data } = await api.post(`/kids/${kidId}/connect/onboard`, {
        return_url: `${origin}/dashboard/parent?kid_connect=return&kid_id=${kidId}`,
        refresh_url: `${origin}/dashboard/parent?kid_connect=refresh&kid_id=${kidId}`,
      });
      if (data?.url) {
        window.location.assign(data.url);
      } else {
        setKidConnectErr((e) => ({ ...e, [kidId]: "Stripe did not return an onboarding URL." }));
      }
    } catch (e) {
      setKidConnectErr((prev) => ({ ...prev, [kidId]: formatApiError(e) || "Could not start bank onboarding." }));
    } finally {
      setKidConnectBusy("");
    }
  };

  // Lazy-load Connect status for every kid with independent banking ON, and
  // any time a kid gets toggled on we fetch their status too. Also handles
  // the return-from-Stripe redirect: ?kid_connect=return&kid_id=<id> forces
  // a fresh status fetch so the "Bank linked" state appears immediately.
  useEffect(() => {
    Object.entries(bankingMap).forEach(([kidId, on]) => {
      if (on && !(kidId in kidConnectMap)) {
        refreshKidConnect(kidId);
      }
    });
  }, [bankingMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-ensure a handle for every kid without one. Idempotent upstream —
  // safe to call every mount, but we still keep a per-session in-flight
  // guard (`ensuredHandlesRef`) so we don't hammer the API on re-renders.
  const ensuredHandlesRef = React.useRef(new Set());
  useEffect(() => {
    if (!Array.isArray(kids) || kids.length === 0) return;
    const missing = kids.filter((k) => !k.handle && !ensuredHandlesRef.current.has(k.id));
    if (missing.length === 0) return;
    (async () => {
      let refreshed = false;
      for (const k of missing) {
        ensuredHandlesRef.current.add(k.id);
        try {
          await api.post(`/kids/${k.id}/ensure-handle`);
          refreshed = true;
        } catch (_e) { /* non-fatal — user can still tap the button */ }
      }
      if (refreshed) {
        try {
          const { data } = await api.get("/kids");
          setKids(data || []);
        } catch (_e) { /* silent */ }
      }
    })();
  }, [kids]);

  useEffect(() => {
    const kcSid = searchParams.get("kid_connect");
    const kidId = searchParams.get("kid_id");
    if (!kcSid || !kidId) return;
    refreshKidConnect(kidId);
    const next = new URLSearchParams(searchParams);
    next.delete("kid_connect");
    next.delete("kid_id");
    setSearchParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (id, approve) => {
    setBusyId(id);
    try { await api.post(`/listings/${id}/parent-approve`, { approve }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusyId(""); }
  };

  if (user === null) return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /><div className="p-20 text-center">Loading…</div></div>;
  if (user === false) return (
    <div className="min-h-screen bg-[#FFFDF7]"><Navbar />
      <div className="p-20 text-center text-slate-500">Please <Link to="/auth?mode=login" className="text-blue-600 font-semibold">sign in</Link></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="pill bg-blue-100 text-blue-800"><ShieldCheck className="w-3 h-3 mr-1" /> Parent control room</div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 mt-3" data-testid="parent-dashboard-heading">
              Welcome back{user.name ? `, ${user.name.split(" ")[0]}` : ""}
            </h1>
            {/* Account email + verification badges — always visible so parents
                know which inbox receives system alerts and password resets. */}
            {user.email && (
              <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="parent-header-identity">
                <span className="inline-flex items-center gap-1.5 text-sm text-slate-600" data-testid="parent-header-email">
                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-mono">{user.email}</span>
                </span>
                {user.email_verified ? (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border bg-emerald-100 text-emerald-800 border-emerald-200"
                    data-testid="badge-email-verified"
                  >
                    <CheckCircle2 className="w-3 h-3" /> Email verified
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border bg-amber-100 text-amber-800 border-amber-200"
                    data-testid="badge-email-unverified"
                  >
                    <Clock className="w-3 h-3" /> Email unverified
                  </span>
                )}
                {(user.is_adult_verified || user.adult_id_verified || user.is_bank_linked || user.stripe_account_status === "verified" || user.bank_last4 || bankLinked) ? (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border bg-emerald-100 text-emerald-800 border-emerald-200"
                    data-testid="badge-id-verified"
                  >
                    <ShieldCheck className="w-3 h-3" /> ID verified
                  </span>
                ) : user.identity_verified ? (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border bg-blue-100 text-blue-800 border-blue-200"
                    data-testid="badge-id-pending"
                  >
                    <Clock className="w-3 h-3" /> ID review in progress
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border bg-slate-100 text-slate-700 border-slate-200"
                    data-testid="badge-id-not-verified"
                  >
                    <ShieldCheck className="w-3 h-3" /> ID not yet verified
                  </span>
                )}
                {!(user.is_adult_verified || user.adult_id_verified || user.identity_verified || user.is_bank_linked || user.stripe_account_status === "verified" || user.bank_last4 || bankLinked) && (
                  <button
                    type="button"
                    onClick={() => setIdVerifyOpen(true)}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-brandTeal hover:bg-brandTealDark text-white text-[11px] font-bold shadow-sm transition"
                    data-testid="get-id-verified-btn"
                  >
                    <ShieldCheck className="w-3 h-3" /> Get Adult Verified
                  </button>
                )}
                {!user.email_verified && (
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendState === "sending" || resendState === "sent"}
                    className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 hover:text-blue-800 underline underline-offset-2 disabled:opacity-60 disabled:no-underline"
                    data-testid="resend-verification-btn"
                  >
                    {resendState === "sending" && (<><Loader2 className="w-3 h-3 animate-spin" /> Sending…</>)}
                    {resendState === "sent" && (<><CheckCircle2 className="w-3 h-3" /> Sent — check your inbox</>)}
                    {resendState === "error" && "Try again"}
                    {resendState === "idle" && "Resend verification email"}
                  </button>
                )}
                {/* Iteration 250 — mobile web view: Alert Bell sits right
                    alongside the verified pill row (desktop uses the
                    top-nav bell instead). */}
                <span className="md:hidden inline-flex" data-testid="parent-header-bell-wrap">
                  <NotificationBell testIdPrefix="parent-header-bell" />
                </span>
              </div>
            )}
            {resendError && (
              <div className="mt-2 text-xs text-rose-700" data-testid="resend-verification-error" role="alert">
                {resendError}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/parent-help" className="btn-secondary text-sm" data-testid="parent-help-link"><HelpCircle className="w-4 h-4" /> Start Up Guide &amp; FAQ</Link>
            <Link to="/orders" className="btn-secondary text-sm" data-testid="parent-orders-link"><ShoppingBag className="w-4 h-4" /> Orders</Link>
            <Link to="/kids" className="btn-secondary text-sm" data-testid="parent-kids-link"><Users className="w-4 h-4" /> Manage kids</Link>
            <KidZoneQuickSwitch kids={kids} />
            <Link to="/pin" className="btn-secondary text-sm" data-testid="parent-pin-link"><Lock className="w-4 h-4" /> {user.has_pin ? "Change PIN" : "Set PIN"}</Link>
            <Link to="/sell" className="btn-primary text-sm" data-testid="parent-sell-link"><Plus className="w-4 h-4" /> New listing</Link>
          </div>
        </div>

        {/* Iteration 250 — retrofit banner prompting existing (incl.
            Home Screen PWA) parents to enable Web Push sale alerts. */}
        <PushEnableBanner />

        {pinResetSuccess && (
          <div className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-200 px-5 py-4 text-sm text-emerald-800 font-semibold flex items-center gap-2" data-testid="pin-reset-success" role="status">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> {pinResetSuccess}
          </div>
        )}

        {revokeError && (
          <div className="mt-6 rounded-2xl bg-rose-50 border border-rose-200 px-5 py-4 text-sm text-rose-800 font-semibold flex items-center gap-2" data-testid="revoke-sessions-error" role="alert">
            <AlertTriangle className="w-4 h-4 text-rose-600" /> {revokeError}
          </div>
        )}

        {/* Verify email nudge — subtle banner shown when the account email
            is not yet confirmed. Dismissible per session. Ties into the            existing handleResendVerification handler so the state
            (idle/sending/sent/error) is shared with the header button. */}
        {user && !user.email_verified && !emailNudgeDismissed && (
          <div
            className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/70 px-5 py-4 flex flex-col md:flex-row md:items-center gap-3"
            data-testid="verify-email-nudge"
            role="status"
          >
            <div className="flex items-start gap-3 flex-1">
              <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex-shrink-0">
                <Mail className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="font-bold text-slate-900 text-sm">Confirm your email address</div>
                <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                  We sent a verification link to{" "}
                  <span className="font-mono font-semibold text-slate-800">{user.email}</span>.
                  Please confirm so payout requests and password resets can reach you.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendState === "sending" || resendState === "sent"}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition disabled:opacity-60"
                data-testid="verify-email-nudge-resend-btn"
              >
                {resendState === "sending" && (<><Loader2 className="w-3 h-3 animate-spin" /> Sending…</>)}
                {resendState === "sent" && (<><CheckCircle2 className="w-3 h-3" /> Sent</>)}
                {(resendState === "idle" || resendState === "error") && (<><Send className="w-3 h-3" /> Resend Email</>)}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEmailNudgeDismissed(true);
                  try { sessionStorage.setItem("mttApp:emailNudgeDismissed", "1"); } catch (_) { /* sessionStorage unavailable */ }
                }}
                className="text-xs text-slate-500 hover:text-slate-800 font-semibold px-2 py-1 rounded"
                data-testid="verify-email-nudge-dismiss-btn"
                aria-label="Dismiss email verification banner"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Mobile-only "Add to Home Screen" banner */}
        <AddToHomeScreenBanner />

        {/* Iteration 95 — In-app "kid edit submitted" ping. Surfaces when
            a child hits Save on a listing edit while the parent tab is
            open. Clicking the banner jumps straight to the Moderation
            Queue so the parent can review the diff and re-approve. */}
        {kidEditPing && (
          <div
            className="mt-6 rounded-2xl border border-violet-300 bg-violet-50 px-5 py-4 flex flex-wrap items-center gap-3 shadow-sm animate-in fade-in slide-in-from-top-1"
            data-testid="kid-edit-ping-banner"
            role="status"
          >
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-violet-200 text-violet-800 flex-shrink-0">
              <Edit3 className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">Kid edit submitted</div>
              <div className="font-bold text-slate-900 text-sm mt-0.5" data-testid="kid-edit-ping-title">
                {kidEditPing.kid_alias} updated &ldquo;{kidEditPing.title}&rdquo; — needs your re-approval.
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={jumpToModerationQueue}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold transition"
                data-testid="kid-edit-ping-review-btn"
              >
                Review now
              </button>
              <button
                type="button"
                onClick={dismissKidEditPing}
                className="text-xs text-slate-500 hover:text-slate-800 font-semibold px-2 py-1 rounded"
                aria-label="Dismiss kid edit notification"
                data-testid="kid-edit-ping-dismiss-btn"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-8" data-testid="parent-stats">
          {[
            { i: <Clock className="w-5 h-5 text-amber-500" />,        t: "Pending approvals", v: stats?.pending_approval ?? "–",  k: "pending",  tab: "pending"  },
            { i: <Box className="w-5 h-5 text-emerald-500" />,        t: "Live listings",     v: stats?.live_listings ?? "–",     k: "live",     tab: "live"     },
            { i: <PoundSterling className="w-5 h-5 text-amber-600" />, t: "Reserved items",   v: stats?.reserved_listings ?? "–", k: "reserved", tab: "reserved" },
            // Iteration 105 — bind EXPLICITLY to the canonical household
            // aggregates from /api/parent/dashboard. No fallback to
            // single-kid stats — if the field is missing, show a dash
            // rather than silently rendering a stale number.
            // Iteration 165 — Orders stat card now routes to `/orders`
            // (Sales & Purchases) so it matches the top-nav Orders link
            // and the dashboard Orders pill. Previously it deep-linked
            // to `/dashboard/ledger?tab=orders` which was a different
            // view than buttons 1 & 2.
            { i: <CheckCircle2 className="w-5 h-5 text-blue-500" />,  t: "Orders",            v: stats?.total_household_orders ?? "–", k: "orders",   tab: "orders", route: "/orders" },
            // Iteration 110 — bind Earnings card directly to the new
            // canonical `/api/ledger/summary` endpoint
            // (`net_earnings_pennies`). Falls back to the household
            // aggregate on `/api/parent/dashboard` when the summary
            // endpoint is unavailable, and finally to a dash rather
            // than silently rendering a stale/gross number.
            { i: <PoundSterling className="w-5 h-5 text-rose-500" />, t: "Earnings",          v: (ledgerSummary && ledgerSummary.net_earnings_pennies !== undefined && ledgerSummary.net_earnings_pennies !== null) ? formatGBP((ledgerSummary.net_earnings_pennies || 0) / 100) : (stats?.total_household_net_earnings_pennies !== undefined ? formatGBP((stats.total_household_net_earnings_pennies || 0) / 100) : "–"), k: "earnings", tab: "earnings" },
            { i: <Box className="w-5 h-5 text-slate-500" />,          t: "History ledger",    v: "View",                          k: "history",  tab: "history"  },
          ].map((s) => {
            // Iteration 259 — the Pending Approvals stat card deep-links
            // straight into the Ledger & Activity page with the Pending
            // Approvals tab selected (/dashboard/ledger?tab=pending),
            // matching the Parent Dashboard → Ledger & Activity → Pending
            // Approvals breadcrumb. It no longer routes to /parent/moderation.
            const commonProps = {
              key: s.k,
              className: "card-base p-4 md:p-5 hover:border-brandSlate hover:shadow-md transition-all cursor-pointer group",
              "data-testid": `parent-stat-${s.k}`,
              "aria-label": `Open ${s.t}`,
            };
            const inner = (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm font-semibold text-slate-500 group-hover:text-brandSlate transition-colors">{s.t}</span>
                  {s.i}
                </div>
                <div className="text-2xl md:text-3xl font-display font-semibold text-slate-900 mt-2">{s.v}</div>
              </>
            );
            if (s.route) {
              return <Link key={s.k} to={s.route} {...commonProps}>{inner}</Link>;
            }
            return <Link key={s.k} to={`/dashboard/ledger?tab=${s.tab}`} {...commonProps}>{inner}</Link>;
          })}
        </div>

        {/* Iteration 290 — Household Impact & Rewards. Four aggregated
            metrics (seller profit, cause donations, buyer savings vs
            estimated retail, landfill weight diverted) + eco badges. */}
        {stats?.impact && (
          <div className="mt-8 card-base p-5" data-testid="parent-impact">
            <h2 className="font-display text-xl font-medium text-slate-900">Household Impact</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
              {[
                { t: "Sales profit received", v: formatGBP((stats.impact.seller_profit_pennies || 0) / 100), sub: "net after platform fee", tId: "parent-impact-profit", tone: "bg-emerald-50 border-emerald-200" },
                { t: "Donated to causes", v: formatGBP((stats.impact.donated_to_causes_pennies || 0) / 100), sub: "from fundraising listings", tId: "parent-impact-donated", tone: "bg-rose-50 border-rose-200" },
                { t: "Money saved buying", v: formatGBP((stats.impact.buyer_savings_pennies || 0) / 100), sub: "vs estimated retail", tId: "parent-impact-saved", tone: "bg-sky-50 border-sky-200" },
                { t: "Diverted from landfill", v: `${(stats.impact.weight_diverted_kg || 0).toFixed(2)} kg`, sub: `${stats.impact.items_rehomed || 0} items rehomed`, tId: "parent-impact-weight", tone: "bg-lime-50 border-lime-200" },
              ].map((c) => (
                <div key={c.tId} className={`rounded-2xl border p-3 ${c.tone}`} data-testid={c.tId}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{c.t}</div>
                  <div className="font-display text-2xl font-semibold text-slate-900 mt-1">{c.v}</div>
                  <div className="text-[11px] text-slate-500">{c.sub}</div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Household Eco Badges</div>
              <div className="flex flex-wrap gap-2" data-testid="parent-eco-badges">
                {(stats.impact.badges || []).map((b) => (
                  <span
                    key={b.key}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${b.unlocked ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}
                    data-testid={`parent-eco-badge-${b.key}`}
                    title={b.unlocked ? `Unlocked — ${b.threshold_kg}kg diverted` : `Diverts at ${b.threshold_kg}kg to unlock`}
                  >
                    {b.unlocked ? <Leaf className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                    {b.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* App Store launch banner — Iteration 99: admin exports moved to /admin.
            Parents always see the friendly banner here. */}
        {(() => {
          // Parent-facing friendly launch banner (no admin counters/exports)
          return (
            <div
              className="card-base mt-6 p-6 bg-gradient-to-br from-brandPink/8 via-white to-brandTeal/8 border-brandTeal/20 flex flex-wrap items-center justify-between gap-4"
              data-testid="app-store-launch-banner"
            >
              <div className="flex items-start gap-4">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brandPink/15 text-brandPink flex-shrink-0">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-brandPink">App Store &amp; Google Play</div>
                  <div className="font-display text-lg font-semibold text-brandSlate mt-0.5">
                    Launching soon on your phone
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {notifyDone
                      ? <>You&rsquo;re on the list — we&rsquo;ll email <strong className="text-brandSlate">{user?.email}</strong> the moment we launch.</>
                      : <>We&rsquo;ll email <strong className="text-brandSlate">{user?.email || "you"}</strong> the moment the mobile apps go live.</>}
                  </div>
                  {notifyError && (
                    <div className="text-xs text-rose-600 mt-1" data-testid="app-store-notify-error">{notifyError}</div>
                  )}
                </div>
              </div>
              {notifyDone ? (
                <div
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-100 text-emerald-800 font-semibold text-sm"
                  data-testid="app-store-notify-done"
                >
                  <CheckCircle className="w-4 h-4" /> You&rsquo;re on the list
                </div>
              ) : (
                <button
                  type="button"
                  onClick={notifyMe}
                  disabled={notifyBusy || !user?.email}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brandTeal text-white font-semibold text-sm hover:bg-brandTealDark transition disabled:opacity-60"
                  data-testid="app-store-notify-btn"
                  title={user?.email ? `Notify me at ${user.email}` : "Notify me"}
                >
                  {notifyBusy ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</>
                  ) : (
                    <><Mail className="w-4 h-4" /> Notify me at {user?.email || "my email"}</>
                  )}
                </button>
              )}
            </div>
          );
        })()}

        {/* Payout link — always visible so new parents can link early */}
        <div className="card-base mt-6 p-6 flex flex-wrap items-center justify-between gap-4" data-testid="payout-link-card">
          <div className="flex items-center gap-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brandTeal/15 text-brandTeal flex-shrink-0">
              <Landmark className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-brandTeal">Payouts</div>
              {/* Iteration 64 — while the mount fetch is in flight the label
                  stays neutral so we never flash "Link your bank" for
                  parents who are already linked. `connectStatus === null`
                  means the first /wallet/connect/status hasn't landed yet. */}
              <div className="font-display text-lg font-semibold text-brandSlate mt-0.5" data-testid="payout-status-label">
                {connectStatus === null
                  ? "Checking your bank status…"
                  : bankLinked
                    ? "Payout details linked"
                    : "Link your bank for payouts"}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {connectStatus === null
                  ? "One-tap link · Powered by Stripe"
                  : bankLinked
                    ? "Payouts land in your Wallet and transfer weekly."
                    : "A one-off family safety check runs before we open your payout form."}
              </div>
            </div>
          </div>
          {connectStatus === null ? (
            <span className="pill bg-slate-100 text-slate-500 inline-flex items-center gap-1" data-testid="payout-status-loading">
              <Loader2 className="w-3 h-3 animate-spin" /> Checking…
            </span>
          ) : bankLinked ? (
            <span className="pill bg-brandTeal/15 text-brandTeal" data-testid="payout-linked-badge">
              <ShieldCheck className="w-3 h-3 mr-1" /> Linked
            </span>
          ) : (
            <button
              type="button"
              onClick={startConnectOnboarding}
              disabled={connectBusy}
              className="btn-teal !py-2 !px-4 text-sm disabled:opacity-60"
              data-testid="wallet-link-payout-btn"
            >
              {connectBusy ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Opening Stripe…</>
              ) : (
                <><Landmark className="w-4 h-4" /> Link payout details</>
              )}
            </button>
          )}
        </div>

        {/* Full-width Family Wallet + Pending Approvals tiles — placed above
            the two-column grid so headings/badges don't collide with the
            narrow "Your kids" sidebar column. */}
        <FamilyWalletTile kidsFallback={kids} walletAvailablePence={walletAvailablePence} refreshKey={walletRefreshKey} onTopUp={quickTopUp} />
        {/* Iteration 103 — Pending Approvals moved to /parent/moderation.
            This card now surfaces the household's Star Ratings & Reviews
            summary and deep-links to the reviews moderation page. */}
        <Link
          to="/dashboard/reviews"
          className="card-base mt-6 p-6 md:p-8 flex flex-wrap items-center justify-between gap-4 hover:border-brandPink hover:shadow-md transition-all"
          data-testid="parent-reviews-summary-card"
          aria-label="Open Star Ratings & Reviews"
        >
          <div className="flex items-start gap-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brandPink/15 text-brandPink flex-shrink-0">
              <Star className="w-6 h-6 fill-current" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-brandPink">Star Ratings &amp; Reviews</div>
              <h2 className="font-display text-xl md:text-2xl font-medium text-slate-900 mt-0.5">
                See what buyers are saying about your kids
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Read every review, hide anything unfair, and celebrate the 5-star raves.
              </p>
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-brandPink" data-testid="parent-reviews-summary-cta">
            Open reviews →
          </div>
        </Link>

        <div className="grid lg:grid-cols-3 gap-6 mt-10">
          {/* Iteration 124 — Unified pending queue replaces both the old
              inline moderation queue and the separate PendingApprovalsInbox
              tile. Every pending item type renders in ONE list, and every
              LISTING card exposes the canonical 4-action strip. */}
          <div className="lg:col-span-2" data-testid="parent-moderation-queue">
            {/* Iteration 260 — Purchase & Auto-Approval Controls sit
                directly above the Pending Approvals queue. */}
            <PurchaseControlsCard kids={kids} />
            <UnifiedPendingQueue
              kidsById={Object.fromEntries((kids || []).map((k) => [k.id, k]))}
            />
          </div>

          <div className="card-base p-6 md:p-8" data-testid="parent-kids-panel">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-medium text-slate-900 flex items-center gap-2"><Users className="w-5 h-5" /> Your kids</h2>
              <Link to="/kids" className="text-sm text-blue-600 font-semibold" data-testid="kids-manage-link">Manage →</Link>
            </div>
            <p className="text-sm text-slate-500">{stats?.kids_count ?? 0} profile{(stats?.kids_count ?? 0) === 1 ? "" : "s"}.</p>
            {bankingError && (
              <div
                className="mt-3 rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800"
                data-testid="banking-toggle-error"
                role="alert"
              >
                {bankingError}
              </div>
            )}
            <div className="mt-4 space-y-3">
              {kids.length === 0 ? (
                <div className="text-sm text-slate-500">No kids yet. <Link to="/kids" className="text-blue-600 font-semibold">Add your first child →</Link></div>
              ) : kids.map((k) => (
                <div key={k.id} className="border border-slate-200 rounded-2xl p-4" data-testid={`kid-row-${k.id}`}>
                  <Link to={`/kids/${k.id}`} className="flex items-center gap-3 hover:opacity-90">
                    <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-2xl flex-shrink-0">{k.avatar_emoji || "🧒"}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900 truncate">{k.name}</div>
                      <div className="text-sm text-slate-500">Age {k.age ?? "—"}</div>
                    </div>
                    <div className="text-right shrink-0" data-testid={`kid-wallet-${k.id}`}>
                      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-700">Wallet</div>
                      <div className="text-lg font-display font-semibold text-emerald-900 leading-none mt-0.5">
                        {formatGBP((k.wallet_pennies ?? 0) / 100)}
                      </div>
                    </div>
                  </Link>
                  {/* Prominent handle display — large, bold, highly legible
                      @handle with a full-label "Copy Handle" button and an
                      inline "Regenerate" affordance. `ensure-handle` is
                      auto-triggered on mount so this always resolves within
                      moments of loading. */}
                  {k.handle ? (
                    <div
                      className="mt-3 rounded-2xl bg-gradient-to-br from-brandTeal/12 via-brandTeal/5 to-transparent border border-brandTeal/25 px-4 py-3"
                      data-testid={`kid-handle-inline-${k.id}`}
                    >
                      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-brandTeal">Kid Zone handle</div>
                      <div
                        className="font-mono font-bold text-brandTealDark text-lg md:text-xl truncate leading-tight mt-0.5"
                        data-testid={`kid-handle-inline-value-${k.id}`}
                        title={`@${k.handle}`}
                      >
                        @{k.handle}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); copyHandle(k.handle); }}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-brandTeal hover:bg-brandTealDark text-white text-xs font-bold shadow-sm transition"
                          title={`Copy @${k.handle} to clipboard`}
                          aria-label={`Copy @${k.handle}`}
                          data-testid={`kid-handle-inline-copy-${k.id}`}
                        >
                          {copiedHandleFor === k.handle
                            ? (<><Check className="w-3.5 h-3.5" /> Copied</>)
                            : (<><Copy className="w-3.5 h-3.5" /> Copy Handle</>)}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRegenerateConfirmKid(k); }}
                          disabled={handlePickerBusy === k.id}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white border border-brandTeal/30 hover:border-brandTeal text-brandTeal transition disabled:opacity-40 flex-shrink-0"
                          data-testid={`kid-handle-regen-${k.id}`}
                          title="Regenerate handle"
                          aria-label="Regenerate handle"
                        >
                          {handlePickerBusy === k.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                        </button>
                      </div>
                      {handlePickerErr[k.id] && (
                        <div className="mt-1.5 text-[11px] text-rose-700" data-testid={`kid-handle-error-${k.id}`}>
                          {handlePickerErr[k.id]}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className="mt-3 rounded-2xl bg-slate-50 border border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
                      data-testid={`kid-handle-picker-${k.id}`}
                    >
                      <div className="text-xs text-slate-600 leading-tight flex-1 min-w-0">
                        No Kid Zone handle yet &mdash; {k.name} can only log in on this device.
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); generateHandle(k.id); }}
                        disabled={handlePickerBusy === k.id}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-brandTeal hover:bg-brandTealDark text-white text-xs font-bold transition disabled:opacity-60 flex-shrink-0"
                        data-testid={`kid-handle-generate-btn-${k.id}`}
                      >
                        {handlePickerBusy === k.id
                          ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating&hellip;</>)
                          : (<><Wand2 className="w-3.5 h-3.5" /> Generate handle</>)}
                      </button>
                      {handlePickerErr[k.id] && (
                        <div className="w-full text-[11px] text-rose-700" data-testid={`kid-handle-error-${k.id}`}>
                          {handlePickerErr[k.id]}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Share Shop Link — copies the child's public storefront URL
                      (https://mytabletopapp.co.uk/shop/:kidId) to the clipboard
                      so parents can share the child's shop in a single tap. */}
                  <button
                    type="button"
                    onClick={() => shareKidShopLink(k)}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-brandPink/10 text-brandPinkDark font-bold text-sm hover:bg-brandPink/20 transition"
                    data-testid={`kid-share-shop-btn-${k.id}`}
                    title={`Copy ${k.name}'s public shop link`}
                    aria-label={`Copy ${k.name}'s shop link`}
                  >
                    {sharedShopFor === k.id
                      ? (<><Check className="w-4 h-4" /> Link copied</>)
                      : (<><Link2 className="w-4 h-4" /> Share Shop Link</>)}
                  </button>
                  {shareShopErr[k.id] && (
                    <div className="mt-1 text-[11px] text-rose-700" data-testid={`kid-share-shop-error-${k.id}`}>
                      {shareShopErr[k.id]}
                    </div>
                  )}
                  {/* Reset PIN — parent-authorised POST /api/kids/{id}/pin.
                      Also revokes any active kid sessions upstream. */}
                  <button
                    type="button"
                    onClick={() => setPinResetKid(k)}
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm transition"
                    data-testid={`kid-reset-pin-btn-${k.id}`}
                  >
                    <KeyRound className="w-4 h-4" /> Reset Kid Zone PIN
                  </button>
                  <button
                    type="button"
                    onClick={() => setRevokeConfirmKid(k)}
                    disabled={revokeBusy === k.id}
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-rose-300 hover:bg-rose-50 text-slate-700 hover:text-rose-700 font-bold text-sm transition disabled:opacity-60"
                    data-testid={`kid-revoke-sessions-btn-${k.id}`}
                    title="Signs the kid out on every device without changing their PIN."
                  >
                    <LogOut className="w-4 h-4" /> Sign kid out on all devices
                  </button>
                  {/* Independent banking toggle — server-truth via PATCH /kids/{id}/independent-banking */}
                  {k.age && k.age >= 13 && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-xs">
                          <Banknote className="w-4 h-4 text-emerald-600" />
                          <span className="font-bold text-slate-700">Independent banking</span>
                        </div>
                        <label
                          className={`inline-flex items-center ${bankingBusyId === k.id ? "cursor-wait opacity-60" : "cursor-pointer"}`}
                          data-testid={`banking-toggle-${k.id}`}
                          aria-busy={bankingBusyId === k.id}
                        >
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={!!bankingMap[k.id]}
                            disabled={bankingBusyId === k.id}
                            onChange={() => toggleBanking(k.id)}
                          />
                          <div className="relative w-10 h-5 bg-slate-300 rounded-full peer-checked:bg-emerald-500 transition-all">
                            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${bankingMap[k.id] ? "translate-x-5" : ""}`} />
                          </div>
                        </label>
                      </div>
                      {/* "Link bank" CTA — only when the toggle is ON. Reads
                          the per-kid Stripe Connect status; renders the
                          appropriate action for not_linked / pending /
                          linked, and passes through the upstream age-gate
                          message (Stripe Express requires the account
                          holder to be 18+). */}
                      {bankingMap[k.id] && (
                        <KidConnectCTA
                          kidId={k.id}
                          kidName={k.name}
                          status={kidConnectMap[k.id]}
                          busy={kidConnectBusy === k.id}
                          err={kidConnectErr[k.id]}
                          onLink={() => linkKidBank(k.id)}
                          onRefresh={() => refreshKidConnect(k.id)}
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Household Wallet — always visible so parents can see balance, top-up, and transfer regardless of revenue */}
        {stats && (
          <div className="card-base p-6 md:p-8 mt-6" data-testid="parent-wallet-widget">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-medium text-slate-900 flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-600" /> Household Wallet</h2>
              <div className="flex items-center gap-2">
                <Link
                  to="/dashboard/wallet-statement"
                  className="text-xs text-blue-700 hover:text-blue-800 font-semibold underline underline-offset-2"
                  data-testid="wallet-view-ledger-link"
                >
                  View Full Ledger →
                </Link>
                <span className="pill bg-emerald-100 text-emerald-800">Live balance</span>
              </div>
            </div>

            {/* Live balance + top-up + withdraw + transfer-to-child */}
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="wallet-balance-row">
              <div className="card-base p-5 bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
                <div className="text-xs text-emerald-800 font-bold uppercase tracking-wider">Available balance</div>
                <div className="text-4xl font-display font-semibold text-emerald-900 mt-1" data-testid="wallet-available-balance">
                  {formatGBP(walletAvailablePence / 100)}
                </div>
                <div className="text-[11px] text-emerald-800 mt-1">Ready to transfer to your children</div>
                {walletPendingPence > 0 && (
                  <div className="text-[11px] text-slate-500 mt-2" data-testid="wallet-pending-balance">
                    Pending: <strong>{formatGBP(walletPendingPence / 100)}</strong>
                  </div>
                )}
                {walletPayoutRequestedPence > 0 && (
                  <div className="text-[11px] text-slate-500 mt-1" data-testid="wallet-payout-requested">
                    Payout requested: <strong>{formatGBP(walletPayoutRequestedPence / 100)}</strong>
                  </div>
                )}
              </div>

              <div className="card-base p-5">
                <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">Add funds</div>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">Top up your household wallet with a card so you can send pocket money to your kids instantly.</p>
                <button
                  type="button"
                  onClick={openTopUp}
                  className="btn-teal w-full justify-center mt-3"
                  data-testid="wallet-topup-btn"
                >
                  <ArrowUpRight className="w-4 h-4" /> Top up wallet
                </button>
              </div>

              <div className="card-base p-5">
                <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">Withdraw funds</div>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">Send available household balance to your linked bank account — PIN protected.</p>
                <button
                  type="button"
                  onClick={onWithdrawClick}
                  disabled={walletAvailablePence <= 0}
                  className="w-full inline-flex items-center justify-center gap-2 mt-3 px-4 py-3 rounded-2xl bg-brandSlate text-white font-bold text-sm hover:bg-brandSlateDark transition disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="wallet-withdraw-btn"
                  title={walletAvailablePence <= 0 ? "Top up first — no available balance" : "Withdraw to your linked bank account"}
                >
                  <Landmark className="w-4 h-4" /> Withdraw Funds
                </button>
                {phaseBEnabled ? (
                  bankLinked ? (
                    /* Iteration 58 — bank is linked, suppress the "Link Bank Account"
                       callout globally and show a compact confirmation pill instead. */
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200"
                      data-testid="wallet-bank-linked-pill"
                    >
                      <CheckCircle className="w-3 h-3" /> Bank linked · payouts ready
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={startConnectOnboarding}
                      disabled={connectBusy}
                      className="mt-2 w-full text-xs font-bold text-brandTeal hover:text-brandTealDark inline-flex items-center justify-center gap-1 disabled:opacity-60"
                      data-testid="wallet-connect-link-btn"
                    >
                      {connectBusy ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Opening Stripe…</>
                      ) : (
                        <>Link my bank via Stripe →</>
                      )}
                    </button>
                  )
                ) : (
                  <div className="mt-2 text-[11px] text-slate-500 leading-relaxed" data-testid="wallet-connect-phase-b-off">
                    <span className="pill bg-slate-100 text-slate-600 !py-0.5 !text-[10px]">Coming soon</span>{" "}
                    Automated bank linking arrives with Phase B. Payouts are currently settled manually by our ops team.
                  </div>
                )}
              </div>

              <div className="card-base p-5">
                <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">Send to child</div>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">Transfer pocket money to one of your children&rsquo;s kid wallets — perfect for chore payouts.</p>
                <button
                  type="button"
                  onClick={openTransfer}
                  disabled={kids.length === 0}
                  className="btn-primary w-full justify-center mt-3 disabled:opacity-60"
                  data-testid="wallet-transfer-btn"
                >
                  <Send className="w-4 h-4" /> Transfer to child
                </button>
                {kids.length === 0 && (
                  <div className="text-[11px] text-slate-500 mt-2">Add a child profile first via Manage kids.</div>
                )}
              </div>
            </div>

            {topUpSuccessMsg && (
              <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-4 py-3" data-testid="wallet-topup-flash">
                {topUpSuccessMsg}
              </div>
            )}
            {connectFlash && (
              <div className="mt-4 rounded-xl bg-brandTeal/10 border border-brandTeal/30 text-brandTealDark text-sm px-4 py-3 flex items-center gap-2" data-testid="wallet-connect-flash">
                <Landmark className="w-4 h-4" /> {connectFlash}
              </div>
            )}

            {/* Collapsible transaction ledger — lazy-fetched on first expand */}
            <div className="mt-6 border-t border-slate-100 pt-4" data-testid="wallet-ledger-section">
              <button
                type="button"
                onClick={toggleLedger}
                className="w-full flex items-center justify-between gap-3 py-2 px-3 rounded-xl hover:bg-slate-50 transition"
                data-testid="wallet-ledger-toggle"
                aria-expanded={ledgerOpen}
              >
                <div className="flex items-center gap-2 text-left">
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-slate-600">
                    <Clock className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-brandSlate">Transaction history</div>
                    <div className="text-[11px] text-slate-500">Top-ups, payouts, and pocket-money sent to your kids</div>
                  </div>
                </div>
                {ledgerOpen
                  ? <ChevronUp className="w-4 h-4 text-slate-500" />
                  : <ChevronDown className="w-4 h-4 text-slate-500" />}
              </button>
              {ledgerOpen && (
                <div className="mt-3 rounded-2xl border border-slate-100 overflow-hidden" data-testid="wallet-ledger-panel">
                  {ledgerLoading && (
                    <div className="p-6 text-sm text-slate-500 flex items-center gap-2" data-testid="wallet-ledger-loading">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading transactions…
                    </div>
                  )}
                  {ledgerError && !ledgerLoading && (
                    <div className="p-4 text-sm text-rose-700 bg-rose-50" data-testid="wallet-ledger-error">{ledgerError}</div>
                  )}
                  {!ledgerLoading && !ledgerError && ledgerTxns.length === 0 && (
                    <div className="p-6 text-sm text-slate-500 text-center" data-testid="wallet-ledger-empty">
                      No transactions yet — your top-ups and transfers will appear here.
                    </div>
                  )}
                  {!ledgerLoading && !ledgerError && ledgerTxns.length > 0 && (
                    <ul className="divide-y divide-slate-100 max-h-80 overflow-auto" data-testid="wallet-ledger-list">
                      {ledgerTxns.map((t, idx) => {
                        const pennies = Number(t.amount_pennies ?? t.pennies ?? 0);
                        const isCredit = (t.direction === "credit") || (pennies > 0 && t.type !== "transfer_to_kid" && t.type !== "payout");
                        const label = t.description || t.note || t.type || "Transaction";
                        const when = t.created_at || t.timestamp || t.date;
                        const dateTxt = when ? new Date(when).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "";
                        return (
                          <li key={t.id || idx} className="flex items-center gap-3 px-4 py-3" data-testid={`wallet-ledger-item-${idx}`}>
                            <div className={`inline-flex items-center justify-center w-9 h-9 rounded-full shrink-0 ${isCredit ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                              {isCredit ? <ArrowDownLeft className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-slate-900 truncate">{label}</div>
                              <div className="text-[11px] text-slate-500">{dateTxt}{t.related_kid_name ? ` · ${t.related_kid_name}` : ""}</div>
                            </div>
                            <div className={`text-sm font-display font-semibold shrink-0 ${isCredit ? "text-emerald-700" : "text-rose-700"}`}>
                              {isCredit ? "+" : "-"}{formatGBP(Math.abs(pennies) / 100)}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Iteration 170 — "Lifetime fee breakdown" section
                (header + 3 cards + disclaimer) removed from the
                Parent Dashboard home per product decision. Fee
                transparency still available in the Fees Info
                modal and per-transaction table below. */}

            {/* Per-transaction breakdown */}
            {payouts.length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-100" data-testid="wallet-payouts-table">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-lg font-semibold text-brandSlate">Sales &amp; purchases</h3>
                  <span className="text-xs text-slate-500">{payouts.length} {payouts.length === 1 ? "transaction" : "transactions"}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 border-b border-slate-100">
                        <th className="py-2 pr-3">Item sold</th>
                        <th className="py-2 pr-3 text-right">Listed price</th>
                        <th className="py-2 pr-3 text-right">Safety fee</th>
                        <th className="py-2 pr-3 text-right">Wallet credit</th>
                      </tr>
                    </thead>
                    <tbody data-testid="wallet-payouts-tbody">
                      {payouts.map((p) => {
                        // Iteration 108 — role-aware row. Sellers see
                        // gross → fee → NET (£3.30 → £0.30 → £3.00);
                        // buyers see gross only (£3.30, no fee, dashes
                        // in the fee/net cells). Uses backend
                        // `net_pennies`/`gross_pennies`/`fee_pennies`
                        // with a defensive `amount − fee` fallback.
                        const isSeller = p.role === "sold" || (p.seller_kid_id && !p.buyer_kid_id) || (p.seller_kid_name && !p.role);
                        const grossPence = Number(p.gross_pennies || p.total_pennies || p.amount_pennies || Math.round((p.amount || 0) * 100));
                        const feePence = Number(p.fee_pennies || p.platform_fee_pennies || 0);
                        const netRaw = Number(p.net_pennies || 0);
                        let netPence = netRaw > 0 ? netRaw : Math.max(grossPence - feePence, 0);
                        // Iteration 109c — never let a seller row render
                        // gross as "net". Fall back to the canonical
                        // flat £0.30 platform fee when backend fee/net
                        // are 0.
                        if (isSeller && netPence >= grossPence && grossPence > 0) {
                          netPence = Math.max(grossPence - 30, 0);
                        }
                        const roleLabel = p.role_label
                          || (isSeller && p.seller_kid_name ? `Sold by ${p.seller_kid_name}` : "")
                          || (!isSeller && p.buyer_kid_name ? `Bought by ${p.buyer_kid_name}` : "");
                        const roleCls = isSeller
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-sky-100 text-sky-800";
                        return (
                          <tr key={p.id} className="border-b border-slate-50" data-testid={`wallet-payout-row-${p.id}`}>
                            <td className="py-3 pr-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                {roleLabel && (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${roleCls}`} data-testid={`wallet-payout-role-${p.id}`}>{roleLabel}</span>
                                )}
                                <div className="font-semibold text-brandSlate line-clamp-1" data-testid={`wallet-payout-title-${p.id}`}>{p.title || p.listing_title || "—"}</div>
                              </div>
                              <div className="text-[11px] text-slate-500 mt-0.5">
                                {new Date(p.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                                {p.kid_name && <> · {p.kid_name}</>}
                              </div>
                            </td>
                            <td className="py-3 pr-3 text-right font-semibold text-slate-800" data-testid={`wallet-payout-price-${p.id}`}>{formatGBP(grossPence / 100)}</td>
                            <td className="py-3 pr-3 text-right" data-testid={`wallet-payout-fee-${p.id}`}>
                              <span className="font-semibold text-slate-800">{isSeller ? formatGBP(feePence / 100) : "—"}</span>
                            </td>
                            <td className="py-3 pr-3 text-right font-display font-semibold text-brandTeal" data-testid={`wallet-payout-credit-${p.id}`}>
                              {isSeller ? (
                                <>{formatGBP(netPence / 100)}<span className="ml-1 text-[10px] text-slate-500 font-semibold">net</span></>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-100" data-testid="wallet-payouts-totals">
                        <th className="py-3 pr-3 text-left font-bold text-brandSlate">Total ({payouts.length})</th>
                        <th className="py-3 pr-3 text-right font-bold text-brandSlate" data-testid="wallet-payouts-total-price">
                          {formatGBP(payouts.reduce((s, p) => s + (Number(p.gross_pennies || p.total_pennies || p.amount_pennies || Math.round((p.amount || 0) * 100))) / 100, 0))}
                        </th>
                        <th className="py-3 pr-3 text-right font-bold text-brandSlate" data-testid="wallet-payouts-total-fees">
                          {formatGBP(payouts.reduce((s, p) => {
                            const isSeller = p.role === "sold" || (p.seller_kid_id && !p.buyer_kid_id);
                            if (!isSeller) return s;
                            return s + (Number(p.fee_pennies || p.platform_fee_pennies || 0)) / 100;
                          }, 0))}
                        </th>
                        <th className="py-3 pr-3 text-right font-display font-bold text-brandTeal" data-testid="wallet-payouts-total-credit">
                          {formatGBP(payouts.reduce((s, p) => {
                            const isSeller = p.role === "sold" || (p.seller_kid_id && !p.buyer_kid_id);
                            if (!isSeller) return s;
                            const gross = Number(p.gross_pennies || p.total_pennies || p.amount_pennies || Math.round((p.amount || 0) * 100));
                            const fee = Number(p.fee_pennies || p.platform_fee_pennies || 0);
                            const netRaw = Number(p.net_pennies || 0);
                            return s + (netRaw > 0 ? netRaw : Math.max(gross - fee, 0)) / 100;
                          }, 0))}
                        </th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {err && <div className="mt-6 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="parent-err-bottom">{err}</div>}
      </section>
      <Footer />
      <ParentVerificationModal {...verificationModalProps} />

      {/* Dedicated "Get Adult Verified" modal — direct-open path so the first
          click always opens even before /auth/me flags hydrate. */}
      <ParentVerificationModal
        open={idVerifyOpen}
        onOpenChange={setIdVerifyOpen}
        reason="Confirm you're the adult parent on this account."
        onVerified={() => setIdVerifyOpen(false)}
      />

      {/* Post-register + banner-driven Add to Home Screen modal (mobile only). */}
      <AddToHomeScreenModal open={a2hsOpen} onOpenChange={setA2hsOpen} />

      {editingListing && (
        <EditListingModal
          listing={editingListing}
          onClose={() => setEditingListing(null)}
          onSaved={(updated) => {
            setEditingListing(null);
            if (updated && updated.id) {
              setQueue((prev) => prev.map((q) => (q.id === updated.id ? { ...q, ...updated } : q)));
            } else {
              load();
            }
          }}
        />
      )}

      {/* Reset Kid Zone PIN modal */}
      <Dialog open={!!pinResetKid} onOpenChange={(o) => { if (!o && !pinResetBusy) { setPinResetKid(null); setPinResetValue(""); setPinResetErr(""); } }}>
        <DialogContent className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden shadow-pop" data-testid="pin-reset-modal">
          <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-brandPink/10 via-white to-brandTeal/10 border-b border-slate-100">
            <div className="pill bg-brandPink/15 text-brandPink"><KeyRound className="w-3 h-3 mr-1" /> Kid Zone PIN</div>
            <DialogHeader className="mt-2 space-y-1">
              <DialogTitle className="font-display text-2xl font-semibold text-brandSlate text-left" data-testid="pin-reset-title">
                {pinResetKid ? `Reset ${pinResetKid.name}'s PIN` : "Reset Kid Zone PIN"}
              </DialogTitle>
              <p className="text-sm text-slate-600 leading-relaxed">
                Pick a new 4-digit PIN for {pinResetKid?.name || "your child"}. Any existing Kid Zone sessions will be signed out immediately.
              </p>
            </DialogHeader>
          </div>
          <div className="px-6 pt-5 pb-6 space-y-4">
            <div>
              <label htmlFor="pin-reset-input" className="text-xs font-bold uppercase tracking-wider text-slate-500">New PIN (4–8 digits)</label>
              <input
                id="pin-reset-input"
                type="password"
                inputMode="numeric"
                pattern="\d*"
                autoComplete="new-password"
                value={pinResetValue}
                onChange={(e) => { setPinResetValue(e.target.value.replace(/\D/g, "").slice(0, 8)); setPinResetErr(""); }}
                placeholder="••••"
                className="w-full mt-1 px-4 py-3 rounded-xl border border-slate-200 text-lg font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-brandPink"
                data-testid="pin-reset-input"
                maxLength={8}
                autoFocus
              />
            </div>
            {pinResetErr && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="pin-reset-error">
                {pinResetErr}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => { setPinResetKid(null); setPinResetValue(""); setPinResetErr(""); }} disabled={pinResetBusy} className="flex-1 btn-secondary disabled:opacity-60" data-testid="pin-reset-cancel-btn">Cancel</button>
              <button type="button" onClick={submitPinReset} disabled={pinResetBusy || pinResetValue.length < 4} className="flex-1 btn-primary disabled:opacity-60" data-testid="pin-reset-submit-btn">
                {pinResetBusy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>) : "Save PIN"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Revoke kid sessions confirm */}
      <Dialog open={!!revokeConfirmKid} onOpenChange={(o) => { if (!o && !revokeBusy) { setRevokeConfirmKid(null); } }}>
        <DialogContent className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden shadow-pop" data-testid="revoke-sessions-modal">
          {(() => {
            // Preserve the last known name across the ~200ms Radix close
            // animation so the dialog copy never flashes blank on confirm.
            if (revokeConfirmKid?.name) { lastRevokeNameRef.current = revokeConfirmKid.name; }
            const nm = revokeConfirmKid?.name || lastRevokeNameRef.current || "your kid";
            return (
              <>
                <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-rose-50 to-white border-b border-slate-100">
                  <div className="pill bg-rose-100 text-rose-800"><AlertTriangle className="w-3 h-3 mr-1" /> Sign out on all devices</div>
                  <DialogHeader className="mt-2 space-y-1">
                    <DialogTitle className="font-display text-2xl font-semibold text-brandSlate text-left">
                      Sign {nm} out everywhere?
                    </DialogTitle>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      This ends every active Kid Zone session for {nm}. Their PIN stays the same &mdash; they just need to sign back in next time.
                    </p>
                  </DialogHeader>
                </div>
                <div className="px-6 pt-5 pb-6 flex gap-2">
                  <button type="button" onClick={() => setRevokeConfirmKid(null)} disabled={!!revokeBusy} className="flex-1 btn-secondary disabled:opacity-60" data-testid="revoke-cancel-btn">Cancel</button>
                  <button type="button" onClick={() => revokeKidSessions(revokeConfirmKid)} disabled={!!revokeBusy} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm disabled:opacity-60 transition" data-testid="revoke-confirm-btn">
                    {revokeBusy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Signing out&hellip;</>) : (<><LogOut className="w-4 h-4" /> Sign out</>)}
                  </button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Handle regenerate confirm */}
      <Dialog open={!!regenerateConfirmKid} onOpenChange={(o) => { if (!o && !handlePickerBusy) { setRegenerateConfirmKid(null); } }}>
        <DialogContent className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden shadow-pop" data-testid="handle-regenerate-modal">
          <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-amber-50 to-white border-b border-slate-100">
            <div className="pill bg-amber-100 text-amber-800"><AlertTriangle className="w-3 h-3 mr-1" /> Change handle</div>
            <DialogHeader className="mt-2 space-y-1">
              <DialogTitle className="font-display text-2xl font-semibold text-brandSlate text-left">
                Give {regenerateConfirmKid?.name} a new handle?
              </DialogTitle>
              <p className="text-sm text-slate-600 leading-relaxed">
                Are you sure? This invalidates the handle your child has been using &mdash; they&rsquo;ll need the new one to sign back in to Kid Zone.
              </p>
            </DialogHeader>
          </div>
          <div className="px-6 pt-5 pb-6 flex gap-2">
            <button type="button" onClick={() => setRegenerateConfirmKid(null)} disabled={!!handlePickerBusy} className="flex-1 btn-secondary disabled:opacity-60" data-testid="handle-regen-cancel-btn">Cancel</button>
            <button type="button" onClick={async () => { const k = regenerateConfirmKid; setRegenerateConfirmKid(null); if (k) await generateHandle(k.id); }} disabled={!!handlePickerBusy} className="flex-1 btn-primary disabled:opacity-60" data-testid="handle-regen-confirm-btn">
              {handlePickerBusy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Regenerating…</>) : (<><Wand2 className="w-4 h-4" /> Regenerate</>)}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Wallet Top-up modal — Stripe Checkout for parent wallet */}
      <Dialog open={topUpOpen} onOpenChange={(o) => { if (!o) { setTopUpOpen(false); setTopUpBusy(false); } }}>
        <DialogContent className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden shadow-pop" data-testid="wallet-topup-modal">
          <div className="relative bg-gradient-to-br from-brandTeal to-brandTealDark px-6 pt-8 pb-6 text-white">
            <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10" />
            <div className="relative flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] opacity-90">
              <ArrowUpRight className="w-4 h-4" /> Household Wallet
            </div>
            <DialogHeader className="relative mt-2 text-left space-y-1">
              <DialogTitle className="font-display text-2xl sm:text-3xl font-semibold text-white">Top up your wallet</DialogTitle>
              <DialogDescription className="text-white/90 text-sm leading-relaxed">
                We&rsquo;ll open a secure Stripe checkout. Your balance updates the moment payment succeeds.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-6 py-6 bg-white space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Amount (£)</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {["5", "10", "25", "50"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setTopUpAmount(v)}
                    className={`pill cursor-pointer ${topUpAmount === v ? "bg-brandTeal text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                    data-testid={`wallet-topup-preset-${v}`}
                  >
                    £{v}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min="1"
                max="500"
                step="0.01"
                value={topUpAmount}
                onChange={(e) => setTopUpAmount(e.target.value)}
                className="input-base"
                placeholder="10.00"
                data-testid="wallet-topup-amount"
                disabled={topUpBusy}
              />
              <div className="text-[11px] text-slate-500 mt-1">Between £1 and £500 per top-up.</div>
            </div>
            {topUpError && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="wallet-topup-error">{topUpError}</div>
            )}
            <button
              type="button"
              onClick={submitTopUp}
              disabled={topUpBusy}
              className="btn-teal w-full justify-center disabled:opacity-60"
              data-testid="wallet-topup-submit"
            >
              {topUpBusy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>) : (<><ArrowUpRight className="w-4 h-4" /> Continue to Stripe</>)}
            </button>
            <button
              type="button"
              onClick={() => setTopUpOpen(false)}
              className="w-full text-sm font-semibold text-slate-500 hover:text-brandSlate transition py-1"
              data-testid="wallet-topup-cancel"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer-to-child modal */}
      <Dialog open={transferOpen} onOpenChange={(o) => { if (!o) { setTransferOpen(false); setTransferBusy(false); } }}>
        <DialogContent className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden shadow-pop" data-testid="wallet-transfer-modal">
          <div className="relative bg-gradient-to-br from-brandPink to-brandPinkDark px-6 pt-8 pb-6 text-white">
            <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10" />
            <div className="relative flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] opacity-90">
              <Send className="w-4 h-4" /> Household Wallet
            </div>
            <DialogHeader className="relative mt-2 text-left space-y-1">
              <DialogTitle className="font-display text-2xl sm:text-3xl font-semibold text-white">Transfer to child</DialogTitle>
              <DialogDescription className="text-white/90 text-sm leading-relaxed">
                Move funds from your household wallet into a child&rsquo;s kid wallet.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-6 py-6 bg-white space-y-4">
            <div className="text-xs text-slate-500">
              Available: <strong className="text-emerald-700" data-testid="wallet-transfer-available">{formatGBP(walletAvailablePence / 100)}</strong>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Child</label>
              <select
                value={transferKidId}
                onChange={(e) => setTransferKidId(e.target.value)}
                className="input-base"
                data-testid="wallet-transfer-kid"
                disabled={transferBusy}
              >
                {kids.map((k) => (
                  <option key={k.id} value={k.id}>{k.avatar_emoji ? `${k.avatar_emoji} ` : ""}{k.name}{k.age ? ` (age ${k.age})` : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Amount (£)</label>
              <input
                type="number"
                min="0.01"
                max="500"
                step="0.01"
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                className="input-base"
                placeholder="5.00"
                data-testid="wallet-transfer-amount"
                disabled={transferBusy}
              />
            </div>
            {transferError && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="wallet-transfer-error">{transferError}</div>
            )}
            {transferSuccess && (
              <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="wallet-transfer-success">{transferSuccess}</div>
            )}
            <button
              type="button"
              onClick={submitTransfer}
              disabled={transferBusy || !transferKidId}
              className="btn-primary w-full justify-center disabled:opacity-60"
              data-testid="wallet-transfer-submit"
            >
              {transferBusy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : (<><KeyRound className="w-4 h-4" /> Confirm with PIN &amp; send</>)}
            </button>
            <button
              type="button"
              onClick={() => setTransferOpen(false)}
              className="w-full text-sm font-semibold text-slate-500 hover:text-brandSlate transition py-1"
              data-testid="wallet-transfer-cancel"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PIN gate for wallet transfers (modal + per-kid quick send) */}
      <PinPromptModal
        open={pinOpen}
        onClose={() => { setPinOpen(false); setPinAction(null); }}
        onVerified={onPinVerified}
        title={pinAction?.kind === "quick-send"
          ? `Send ${formatGBP((pinAction?.pennies || 100) / 100)} to ${pinAction?.kidName || "your child"}`
          : `Confirm transfer${pinAction ? ` of ${formatGBP((pinAction.pennies || 0) / 100)} to ${pinAction.kidName}` : ""}`}
        description="Enter your 4-digit parent PIN to authorise this transfer."
        confirmLabel={pinAction?.kind === "quick-send"
          ? `Send ${formatGBP((pinAction?.pennies || 100) / 100)}`
          : "Send now"}
        testIdPrefix="wallet-pin"
      />

      {/* Withdraw / Payout modal — Phase A */}
      <Dialog open={withdrawOpen} onOpenChange={(o) => { if (!o && !withdrawBusy) setWithdrawOpen(false); }}>
        <DialogContent className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden shadow-pop" data-testid="wallet-withdraw-modal">
          <div className="relative bg-gradient-to-br from-brandSlate to-slate-800 px-6 pt-8 pb-6 text-white">
            <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10" />
            <div className="relative flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] opacity-90">
              <Landmark className="w-4 h-4" /> Household Wallet
            </div>
            <DialogHeader className="relative mt-2 text-left space-y-1">
              <DialogTitle className="font-display text-2xl sm:text-3xl font-semibold text-white">Withdraw funds</DialogTitle>
              <DialogDescription className="text-white/90 text-sm leading-relaxed">
                Send available balance to your linked bank account. Payouts usually settle within 1&ndash;2 business days.
              </DialogDescription>
            </DialogHeader>
          </div>
          <form
            className="px-6 py-6 bg-white space-y-4"
            noValidate
            onSubmit={(e) => { e.preventDefault(); submitWithdraw(); }}
          >
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-700">Available balance</div>
                <div className="text-2xl font-display font-semibold text-emerald-900 mt-0.5" data-testid="wallet-withdraw-available">
                  {formatGBP(walletAvailablePence / 100)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setWithdrawAmount("")}
                className="text-xs font-bold text-emerald-700 hover:text-emerald-900 underline"
                data-testid="wallet-withdraw-full"
              >
                Full withdrawal
              </button>
            </div>

            {/* Payout destination — reads bank_name / bank_last4 from /auth/me.
                Falls back to a generic "linked bank account" copy so the modal
                still makes sense before the upstream field ships. */}
            <div
              className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 flex items-center gap-3"
              data-testid="wallet-withdraw-destination"
            >
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brandSlate text-white flex-shrink-0">
                <Landmark className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Payout destination</div>
                {user?.bank_last4 ? (
                  <div className="text-sm font-display font-semibold text-brandSlate mt-0.5 truncate" data-testid="wallet-withdraw-destination-label">
                    {user.bank_name || "Linked bank"} <span className="text-slate-500 font-normal">•••• {user.bank_last4}</span>
                  </div>
                ) : (
                  <div className="text-sm font-display font-semibold text-brandSlate mt-0.5" data-testid="wallet-withdraw-destination-label">
                    {phaseBEnabled ? "No bank linked yet" : "Manual payout (Phase A)"}
                  </div>
                )}
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {phaseBEnabled
                    ? <>Settles in 1&ndash;2 business days via Stripe.</>
                    : phaseBOffMessage}
                </div>
              </div>
              {phaseBEnabled && !bankLinked && (
                <button
                  type="button"
                  onClick={startConnectOnboarding}
                  disabled={connectBusy}
                  className="text-xs font-bold text-brandTeal hover:text-brandTealDark inline-flex items-center gap-1 disabled:opacity-60"
                  data-testid="wallet-withdraw-connect-btn"
                  title="Link a bank account via Stripe Connect"
                >
                  {connectBusy ? (
                    <><Loader2 className="w-3 h-3 animate-spin" /> Opening…</>
                  ) : (
                    <>Link my bank</>
                  )}
                </button>
              )}
            </div>
            {connectError && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-2" data-testid="wallet-connect-error">{connectError}</div>
            )}
            <div>
              <label htmlFor="withdraw-amount" className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">
                Amount (£) — leave blank for full withdrawal
              </label>
              <input
                id="withdraw-amount"
                type="number"
                min="1"
                max="500"
                step="0.01"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                className="input-base"
                placeholder="Blank = withdraw all available"
                data-testid="wallet-withdraw-amount"
                disabled={withdrawBusy}
              />
              <div className="text-[11px] text-slate-500 mt-1">Between £1.00 and £500.00 per payout.</div>
            </div>
            <div>
              <label htmlFor="withdraw-pin" className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">
                4-digit security PIN
              </label>
              <input
                id="withdraw-pin"
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                value={withdrawPin}
                onChange={(e) => setWithdrawPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className="input-base text-2xl tracking-[0.6em] text-center font-display"
                placeholder="••••"
                data-testid="wallet-withdraw-pin"
                disabled={withdrawBusy}
              />
            </div>
            {withdrawError && (
              <div
                className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3"
                data-testid="wallet-withdraw-error"
                data-error-code={withdrawErrorCode || undefined}
              >
                {withdrawError}
              </div>
            )}
            {withdrawSuccess && (
              <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="wallet-withdraw-success">
                {withdrawSuccess}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setWithdrawOpen(false)}
                disabled={withdrawBusy}
                className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-2xl border border-slate-200 bg-white text-slate-700 font-bold text-sm hover:bg-slate-50 transition disabled:opacity-50"
                data-testid="wallet-withdraw-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={withdrawBusy || withdrawPin.length !== 4 || walletAvailablePence <= 0}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-brandSlate text-white font-bold text-sm hover:bg-brandSlateDark transition disabled:opacity-50"
                data-testid="wallet-withdraw-submit"
              >
                {withdrawBusy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>) : (<><Landmark className="w-4 h-4" /> Confirm Withdrawal</>)}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Stripe Connect Embedded Components — inline bank linking */}
      <ConnectOnboardModal
        open={connectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        onExit={onConnectExit}
      />
    </div>
  );
}
